import * as d3 from 'd3'
import {
  clampPercent,
  snapPercentToStep,
  deepZoomPercent,
  percentToDeepZoomScale,
  fitZoomMaxScale,
  ZOOM_MIN_PERCENT,
  ZOOM_MAX_PERCENT_VECTOR,
} from '@/constants/zoom'
import { nextStepPercent } from '@/hooks/useZoom'
import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { ModeType } from '@/types/graph'
import { parseLineStringSegments } from '@/service/graph'
import { bboxOfVertices, moveVertex, insertVertexAfter, deleteVertexAt } from './polygonEdit'
import { computeCmdClickSelection } from './multiSelect'
import { ZoomControls, type ZoomControlsUndoRedo } from '@/components/intueri/ZoomControls'
import { shouldD3HandleGesture, isCanvasPanPress, shouldSpaceArmPan } from './zoomGesture'
import { pointInPolygon, nearestPointOnPath, type Pt } from './shapeSnap'
import { dockConnectionEndpoints, type DockShape } from './dockGeometry'
import { colors } from '@/styles/colors'
import { useTranslation } from '@/i18n'
import EditorLayersOverlay from './EditorLayersOverlay'
import { shownLayers, type EditorLayers } from './editorLayers'

export interface Rectangle {
  id: string
  x: number
  y: number
  width: number
  height: number
  color: string
  zIndex: number
  geometry_wkt?: string // Optional LINESTRING for connection-type nodes
  vertices?: Array<{ x: number; y: number }> // Freeform polygon outline (ENG-2582)
  isJunction?: boolean // T/junction point — rendered as a small dot, not a box (ENG-2613)
  isManual?: boolean // ENG-2622: annotator-drawn — rendered with a dashed outline to distinguish from solid pipeline/OCR detections
  opacity?: number // Per-shape opacity override (Review layer mutes non-flagged detections); falls back to the focus-mode dim when unset
}

// ENG-2622: dash pattern for hand-drawn (manual) objects. Kept off the solid
// detection stroke so the two provenance classes are visually unambiguous.
const MANUAL_STROKE_DASHARRAY = '14 8'

// FS-623: neon-magenta spotlight for review/filter-highlighted objects (replaces the
// old gentle amber/gold, which was hard to spot on dense drawings). Magenta, not
// green — green already means "detected"/connection-hover on the canvas, so a green
// halo blended in. Distinct from the canvas blue (links), red (editing), cyan
// (markers) too. Shared by the Pending Review panel and the labeling filter — both
// feed `reviewHighlightIds`.
const SPOTLIGHT_RING = '#FF00FF'
// FS-623 follow-up (Armin): the 0.18 fill was too faint to read against a dense
// drawing — brighten the match's glow so it clearly stands out (esp. in focus mode).
const SPOTLIGHT_GLOW = 'rgba(255, 0, 255, 0.4)'

export interface ClickEvent {
  nodeId?: string
  svg: { x: number; y: number }
  screen: { x: number; y: number }
  group?: { x: number; y: number }
  absolute?: { x: number; y: number }
  // True when Shift was held (ENG-2583): place a free-angle connection vertex
  // instead of orthogonal-snapping the segment to horizontal/vertical.
  shiftKey?: boolean
}

export interface DeleteEvent {
  nodeIds: string[]
}

export interface AddEvent {
  x: number
  y: number
  width: number
  height: number
}

interface Point {
  x: number
  y: number
}

interface DragState {
  id: string
  startX: number
  startY: number
  initialRectX: number
  initialRectY: number
}

interface ResizeState {
  id: string
  handle: string // 'tl', 'tr', 'bl', 'br', 't', 'r', 'b', 'l', or 'vertex:<i>' (ENG-2582)
  startX: number
  startY: number
  initialRect: {
    x: number
    y: number
    width: number
    height: number
  }
  // ENG-2582: snapshot of the polygon ring at drag start, present only when a
  // vertex handle is being dragged. A vertex is just a special resize handle, so
  // it rides the exact same drag/commit path detected valves use.
  initialVertices?: Array<{ x: number; y: number }>
}

const VERTEX_HANDLE_PREFIX = 'vertex:'

// ENG-2609: edit handles (polygon vertices + rectangle corners) are available
// for a *selected* shape in every tool mode — Select, Create and Drag — not
// just Select. This is what makes vertex editing the default right after a
// draw: on draw-completion the shape is auto-selected, but the Add-Object panel
// closes on that selection and the editor flips back to the Drag/pan tool, so
// gating on Select/Create alone made the freshly drawn shape's handles vanish a
// beat after they appeared. Handles still only render where something is
// selected; whole-shape body-drag stays Select-only (handleRectanglePointerDown)
// so this never enables accidental moves. Read-only and connection-draw are
// excluded at the call sites (lines use a separate edit path).
const canEditShapeHandles = (mode: ModeType, isReadOnly: boolean | undefined): boolean =>
  !isReadOnly &&
  (mode === ModeType.Select || mode === ModeType.Create || mode === ModeType.Drag)

/**
 * Parse a WKT LINESTRING into an array of points.
 * Format: "LINESTRING (x1 y1, x2 y2, x3 y3, ...)"
 */
export const parseLineString = (wkt: string): Point[] => {
  try {
    const match = wkt.match(/LINESTRING\s*\(([^)]+)\)/)
    if (!match) return []
    
    const coordString = match[1]
    const points = coordString.split(',').map(pair => {
      const [x, y] = pair.trim().split(/\s+/).map(Number)
      return { x, y }
    })
    
    return points.filter(p => !isNaN(p.x) && !isNaN(p.y))
  } catch (error) {
    console.error('Failed to parse LINESTRING:', error)
    return []
  }
}

/** Serialize points back to a WKT LINESTRING (inverse of parseLineString). */
export const toLineStringWkt = (points: Point[]): string =>
  `LINESTRING (${points.map((p) => `${p.x} ${p.y}`).join(', ')})`

/**
 * Find the closest point on a line segment to a given point.
 * Returns the closest point and its distance from the given point.
 */
const closestPointOnSegment = (
  px: number, py: number, // The point we're measuring from
  x1: number, y1: number, // Segment start
  x2: number, y2: number  // Segment end
): { x: number; y: number; distance: number } => {
  const dx = x2 - x1
  const dy = y2 - y1
  const segmentLengthSquared = dx * dx + dy * dy
  
  if (segmentLengthSquared === 0) {
    // Segment is a point
    const distance = Math.sqrt((px - x1) * (px - x1) + (py - y1) * (py - y1))
    return { x: x1, y: y1, distance }
  }
  
  // Calculate projection of point onto the line, clamped to segment
  let t = ((px - x1) * dx + (py - y1) * dy) / segmentLengthSquared
  t = Math.max(0, Math.min(1, t)) // Clamp to [0, 1]
  
  const closestX = x1 + t * dx
  const closestY = y1 + t * dy
  const distance = Math.sqrt((px - closestX) * (px - closestX) + (py - closestY) * (py - closestY))
  
  return { x: closestX, y: closestY, distance }
}

/**
 * Find the closest point on a polyline (array of points) to a given point.
 * Iterates through all segments and returns the point with minimum distance.
 */
const closestPointOnPolyline = (
  px: number, py: number, // The point we're measuring from
  points: Point[]
): Point | null => {
  if (points.length === 0) return null
  if (points.length === 1) return points[0]
  
  let closestPoint = { x: points[0].x, y: points[0].y }
  let minDistance = Infinity
  
  for (let i = 0; i < points.length - 1; i++) {
    const result = closestPointOnSegment(
      px, py,
      points[i].x, points[i].y,
      points[i + 1].x, points[i + 1].y
    )
    
    if (result.distance < minDistance) {
      minDistance = result.distance
      closestPoint = { x: result.x, y: result.y }
    }
  }
  
  return closestPoint
}

const getSvgCoordinates = (
  svg: SVGSVGElement,
  clientX: number,
  clientY: number
): Point => {
  const point = svg.createSVGPoint()
  point.x = clientX
  point.y = clientY
  return point.matrixTransform(svg.getScreenCTM()?.inverse())
}

const getTrueSvgCoordinates = (
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
  transform: d3.ZoomTransform
): Point => {
  const svgCoordinates = getSvgCoordinates(svg, clientX, clientY)
  return {
    x: (svgCoordinates.x - transform.x) / transform.k,
    y: (svgCoordinates.y - transform.y) / transform.k,
  }
}

/** Padding (px) kept around the P&ID when fitting it to the viewport. */
const FIT_PADDING_PX = 40

/**
 * Fit-to-view scale — the d3 transform `k` that frames the whole image in the
 * viewport with uniform padding, at this surface's 100% baseline
 * (`initialZoom * 0.9`). Single source of truth for the fit formula shared by
 * the initial load, the resize handler, and the Fit button (ENG-2588: the
 * formula previously lived in several places and could drift).
 */
const computeFitScale = (
  svgWidth: number,
  svgHeight: number,
  imageWidth: number,
  imageHeight: number,
  initialZoom: number,
): number => {
  const availableWidth = svgWidth - FIT_PADDING_PX * 2
  const availableHeight = svgHeight - FIT_PADDING_PX * 2
  return Math.min(availableWidth / imageWidth, availableHeight / imageHeight) * initialZoom * 0.9
}

/** The zoom actions a host's own zoom bar can drive this canvas with. */
export interface PnidZoomHandle {
  zoomIn: () => void
  zoomOut: () => void
  setPercent: (percent: number) => void
  fit: () => void
}

export interface PnidRendererProps {
  backgroundImage: string | null
  rectangles: Rectangle[]
  edges?: Array<{ id?: string; fromNodeId: string; toNodeId: string; relation?: string }>
  nodes?: Map<string, { type?: string }>
  initialZoom?: number
  onClick?: (event: ClickEvent) => void
  onRectangleDelete?: (event: DeleteEvent) => void
  onRectangleAdd?: (event: AddEvent) => void
  /** Fires with the loaded background image's natural pixel size (ENG-2595: GT-export normalization space). */
  onImageSize?: (size: { width: number; height: number }) => void
  onPolygonAdd?: (vertices: Array<{ x: number; y: number }>) => void
  children?: React.ReactNode
  isReadOnly?: boolean
  onRectangleUpdate?: (updatedRectangles: Rectangle[]) => void
  /**
   * A box resize finished. `handle` says WHICH walls moved (`tl`/`tr`/`bl`/`br`/`t`/`r`/`b`/`l`)
   * and `before`/`after` carry the box on either side of the gesture. The table-cell path
   * needs all three to move the whole column/row boundary rather than the one cell's wall.
   *
   * None of it is recoverable by the listener: `onRectangleUpdate` above has already
   * committed the new geometry, and the handler's own view of the nodes is a render-time
   * closure that still shows the PRE-drag box — so a listener that tried to diff the two
   * itself would measure a delta of zero. Hence passing them explicitly.
   */
  onRectangleResizeComplete?: (
    rectangleId: string,
    handle?: string,
    before?: { x: number; y: number; width: number; height: number },
    after?: { x: number; y: number; width: number; height: number },
  ) => void
  mode: ModeType
  selectedNodeId?: string | null
  /**
   * The node whose details panel is currently open (ENG-2624). Used to seed a
   * Cmd/Ctrl+click multi-selection so the open node is folded in. Distinct from
   * `selectedNodeId`, which also reflects search / cross-sheet highlights that
   * the user hasn't actively selected — those must NOT leak into the selection.
   */
  panelSelectedId?: string | null
  isCrossSheetHighlight?: boolean
  suppressNodeClickActions?: boolean
  disableCreate?: boolean
  isPolygonMode?: boolean
  /** Point/junction draw mode (ENG-2613): a click drops a small symbol dot. */
  isPointMode?: boolean
  isConnectionMode?: boolean
  connectionStartPointAbs?: { x: number; y: number } | null
  connectionVertices?: Array<{ x: number; y: number }>
  /** The in-progress line's node id, so the connection hit-test never ends it on itself. */
  connectionLineId?: string | null
  connectionHoveredId?: string | null
  onConnectionHover?: (nodeId: string | null) => void
  /** Double-click in connection mode finishes the in-progress line. */
  onConnectionFinish?: () => void
  onSelectionChange?: (selectedIds: Set<string>) => void
  externalSelectedNodeIds?: Set<string>
  /** Node IDs checked in the Pending panel — drawn with an amber attention halo and framed into view (FS-523). */
  reviewHighlightIds?: Set<string>
  /** FS-623 follow-up: when true and a highlight set is active, dim every NON-matched
   *  shape so the spotlighted matches stand alone ("focus" mode from the filter panel). */
  dimUnmatched?: boolean
  /** Review layer: hide the red tag→connection association leader lines (a detected
   *  overlay) so they don't stand out over the muted detections. */
  hideAssociationLines?: boolean
  /** VISN-360: read-only overlays (flow direction, label links by rule, pipe line numbers). With
   *  text links on, their coloured lines replace the red tag→connection lines. */
  layers?: EditorLayers
  connectionMarkers?: {
    connectedPoints: Array<{ x: number; y: number; markerId: string; symbolId: string; lineId: string }>
    danglingPoints: Array<{ x: number; y: number; markerId: string; lineId: string }>
    candidatePoints: Array<{ x: number; y: number; lineId: string; symbolId: string | null }>
  } | null
  detectedBoundary?: {
    x: number; y: number; width: number; height: number
  } | null
  onMarkerDrop?: (
    markerId: string,
    candidate: { x: number; y: number; lineId: string; symbolId: string | null },
  ) => void
  /**
   * UI-30 (Putra): undo/redo belong in the zoom pill, where the board puts
   * them — not in the vertical tool rail. Passed straight through to the
   * shared <ZoomControls>, so the two surfaces render the same chrome from the
   * same component. Omitted in read-only, where there is nothing to undo.
   */
  undoRedo?: ZoomControlsUndoRedo
  /**
   * Draw no zoom pill of its own. For a host that draws the same bar in its
   * own chrome (the tag-mapping preview overlay) and drives this canvas
   * through `zoomHandleRef` — two pills in one corner would fight.
   */
  hideZoomControls?: boolean
  /** The zoom actions, for a host's own zoom bar. Assigned once the canvas is up. */
  zoomHandleRef?: React.MutableRefObject<PnidZoomHandle | null>
  /** The readout, for a host's own zoom bar: follows wheel zoom as well as buttons. */
  onZoomChange?: (percent: number) => void
}

// Convert SVG coordinates to rectangle coordinates.
const svgToRectCoordinates = (
  svgCoordinates: Point,
  imageSize: { width: number; height: number } | null
): Point => {
  return {
    x: svgCoordinates.x + (imageSize?.width || 0) / 2,
    y: svgCoordinates.y + (imageSize?.height || 0) / 2,
  }
}

/**
 * Is point `p` inside box `b` (inclusive)? Used to confirm a connection-mode click
 * actually lands inside the hovered symbol before attributing the click to it, so a
 * stale hover can't terminate/link the line on a click that's really in open space.
 * `margin` (image-space px) optionally grows the box so a click a hair off the edge
 * — or on the symbol's stroke — still counts.
 */
export const isPointInBox = (
  p: { x: number; y: number },
  b: { x: number; y: number; width: number; height: number },
  margin = 0
): boolean =>
  p.x >= b.x - margin && p.x <= b.x + b.width + margin && p.y >= b.y - margin && p.y <= b.y + b.height + margin

const MIN_DRAG_DISTANCE_IN_PIXELS = 5
// Image-px box dropped for a placed junction dot. Matches the size the ML pipeline
// gives a detected junction — `_create_junction` builds it with margin=5 → a 10px
// footprint (line_detector.py) — so a placed junction is the same size as a detected
// one rather than the old oversized 24px dot. Calibrated to Aramco drawing DPI — see
// docs/tenant-assumptions.md.
const JUNCTION_POINT_SIZE = 10
// Extra screen-px slop when hit-testing a connection draw that ends ON a junction: the
// 10px dot is too small for the default slop to catch reliably. Same DPI caveat above.
const JUNCTION_CONNECT_SLOP_PX = 16

// Margin around a tag box within which a connection endpoint is treated as
// docking at the tag, so the redundant association line into the box is
// suppressed. Covers the tag's box stroke + click slop. Calibrated to Aramco
// drawing density — see docs/tenant-assumptions.md.
const ASSOC_SUPPRESS_MARGIN_PX = 12

const PnidRenderer = ({
  backgroundImage,
  onImageSize,
  rectangles,
  edges = [],
  nodes,
  initialZoom = 1,
  onClick,
  onRectangleDelete,
  onRectangleAdd,
  onPolygonAdd,
  children,
  isReadOnly,
  onRectangleUpdate,
  onRectangleResizeComplete,
  mode,
  selectedNodeId,
  panelSelectedId,
  isCrossSheetHighlight,
  suppressNodeClickActions,
  disableCreate,
  isPolygonMode,
  isPointMode,
  isConnectionMode,
  connectionStartPointAbs,
  connectionVertices = [],
  connectionLineId,
  connectionHoveredId,
  onConnectionHover,
  onConnectionFinish,
  onSelectionChange,
  externalSelectedNodeIds,
  reviewHighlightIds,
  dimUnmatched,
  hideAssociationLines,
  layers,
  connectionMarkers,
  detectedBoundary,
  onMarkerDrop,
  undoRedo,
  hideZoomControls = false,
  zoomHandleRef,
  onZoomChange,
}: PnidRendererProps) => {
  const { t } = useTranslation()
  // Displayed zoom % — a plain counter so the readout steps a clean +/-10 per
  // click (100 → 110 → 120 / 100 → 90 → 80), independent of the continuous d3
  // zoom whose k/baseScale ratio rounds to drifting values (e.g. 94 / 129).
  const [displayZoom, setDisplayZoom] = useState(100)
  const svgRef = useRef<SVGSVGElement>(null)
  const groupRef = useRef<SVGGElement>(null)
  const imageRef = useRef<SVGImageElement>(null)
  const [imageSize, setImageSize] = useState<{
    width: number
    height: number
  } | null>(null)
  const [transform, setTransform] = useState<d3.ZoomTransform>(d3.zoomIdentity)
  // Gates canvas visibility until the first fit-to-view transform is applied,
  // so the user never sees the SVG at the initial identity transform (which
  // shows a zoomed-in crop of the image).
  const [isFitReady, setIsFitReady] = useState(false)
  const isFitReadyRef = useRef(false)
  const baseScaleRef = useRef<number>(1)
  const [selectedRectangles, setSelectedRectangles] = useState<Set<string>>(
    new Set()
  )
  const [selectionStart, setSelectionStart] = useState<{
    x: number
    y: number
  } | null>(null)
  const [selectionEnd, setSelectionEnd] = useState<{
    x: number
    y: number
  } | null>(null)

  // Ref for the zoom behavior.
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null)
  // Ref to hold the current transform without triggering re-renders.
  const transformRef = useRef<d3.ZoomTransform>(d3.zoomIdentity)
  // Last observed SVG viewport size — used by the resize handler to keep the
  // currently-centered point centered when the container changes size.
  const prevSizeRef = useRef<{ w: number; h: number } | null>(null)
  // True while a programmatic zoom/pan (zoomToRectangle) transition is running.
  // The resize handler must not touch the transform during one, otherwise the
  // details-panel-open resize in UIv2 stomps the symbol pan with a fit-to-view.
  const programmaticZoomRef = useRef(false)
  // Ref to throttle state updates.
  const animatingRef = useRef(false)
  // Ref to hold drag state for performance
  const dragRef = useRef<DragState | null>(null)
  // Ref to hold resize state for performance
  const resizeRef = useRef<ResizeState | null>(null)

  const focusedNodeRef = useRef<string | null>(null)
  const isDraggingRef = useRef(false)

  // ENG-2588: live tool/mode + Spacebar state read by the d3 zoom gesture
  // filter. The filter is created once (it must not rebind on every mode
  // change), so it reads the current mode via a ref instead of a stale closure.
  const modeRef = useRef(mode)
  const spacePanningRef = useRef(false)
  const [spacePanning, setSpacePanning] = useState(false)
  useEffect(() => {
    modeRef.current = mode
  }, [mode])
  const isResizingRef = useRef(false)
  const lastBackgroundImageRef = useRef<string | null>(null)
  const [connectionPreview, setConnectionPreview] = useState<Point | null>(null)
  // Whether Shift is held during the connection preview — when true the cursor
  // segment is orthogonal-snapped (matches the commit behavior; ENG-2583).
  const [previewShiftKey, setPreviewShiftKey] = useState(false)
  // Freeform polygon draw (ENG-2582). Vertices are in absolute (image) coords;
  // the cursor drives the rubber-band segment to the next vertex.
  const [polygonVertices, setPolygonVertices] = useState<Array<{ x: number; y: number }>>([])
  const [polygonCursor, setPolygonCursor] = useState<{ x: number; y: number } | null>(null)
  // Marker drag state — active when user is dragging a green/red dot to a new candidate
  const [draggingMarkerId, setDraggingMarkerId] = useState<string | null>(null)
  const [dragCursorPos, setDragCursorPos] = useState<Point | null>(null)
  const draggingMarkerIdRef = useRef<string | null>(null)
  const dragCursorPosRef = useRef<Point | null>(null)
  const connectionMarkersRef = useRef(connectionMarkers)
  const onMarkerDropRef = useRef(onMarkerDrop)
  const imageSizeRef = useRef(imageSize)
  const transformRefForDrag = useRef(transform)
  useEffect(() => { draggingMarkerIdRef.current = draggingMarkerId }, [draggingMarkerId])
  useEffect(() => { dragCursorPosRef.current = dragCursorPos }, [dragCursorPos])
  useEffect(() => { connectionMarkersRef.current = connectionMarkers }, [connectionMarkers])
  useEffect(() => { onMarkerDropRef.current = onMarkerDrop }, [onMarkerDrop])
  useEffect(() => { imageSizeRef.current = imageSize }, [imageSize])
  useEffect(() => { transformRefForDrag.current = transform }, [transform])

  // Global window listeners active while dragging a connection marker.
  // We attach on drag-start and detach on drag-end so the canvas D3 zoom
  // doesn't pan while the user is trying to move a dot.
  useEffect(() => {
    if (!draggingMarkerId) return

    const handleWindowMove = (event: PointerEvent) => {
      event.preventDefault()
      event.stopPropagation()
      const svg = svgRef.current
      const size = imageSizeRef.current
      if (!svg || !size) return
      const truePt = getTrueSvgCoordinates(svg, event.clientX, event.clientY, transformRefForDrag.current)
      setDragCursorPos({
        x: truePt.x + size.width / 2,
        y: truePt.y + size.height / 2,
      })
    }

    const handleWindowUp = (event: PointerEvent) => {
      event.preventDefault()
      event.stopPropagation()
      const markerId = draggingMarkerIdRef.current
      const cursor = dragCursorPosRef.current
      const markers = connectionMarkersRef.current
      const size = imageSizeRef.current
      const drop = onMarkerDropRef.current
      if (markerId && cursor && markers && size) {
        const markerRadius = Math.max(size.width, size.height) * 0.006
        const snapThreshold = markerRadius * 3
        let nearest: { x: number; y: number; lineId: string; symbolId: string | null } | null = null
        let bestDist = Infinity
        for (const c of markers.candidatePoints) {
          const d = Math.hypot(c.x - cursor.x, c.y - cursor.y)
          if (d < bestDist && d <= snapThreshold) {
            bestDist = d
            nearest = c
          }
        }
        if (nearest && drop) drop(markerId, nearest)
      }
      setDraggingMarkerId(null)
      setDragCursorPos(null)
    }

    window.addEventListener('pointermove', handleWindowMove, { capture: true })
    window.addEventListener('pointerup', handleWindowUp, { capture: true })
    return () => {
      window.removeEventListener('pointermove', handleWindowMove, { capture: true })
      window.removeEventListener('pointerup', handleWindowUp, { capture: true })
    }
  }, [draggingMarkerId])

  // Local state for temporary rectangle during drag/resize (like preview during drawing)
  const [tempRectangle, setTempRectangle] = useState<Rectangle | null>(null)

  // Sync local selection with external selection (from parent)
  useEffect(() => {
    if (externalSelectedNodeIds !== undefined) {
      setSelectedRectangles(externalSelectedNodeIds)
    }
  }, [externalSelectedNodeIds])

  // Load background image and set dimensions.
  useEffect(() => {
    if (backgroundImage !== lastBackgroundImageRef.current) {
      lastBackgroundImageRef.current = backgroundImage || null
      setImageSize(null)
      setIsFitReady(false)
      isFitReadyRef.current = false
    }
    if (backgroundImage) {
      const img = new Image()
      img.src = backgroundImage
      img.onload = () => {
        const width = img.width
        const height = img.height
        setImageSize({ width, height })
        onImageSize?.({ width, height })

        if (
          svgRef.current &&
          svgRef.current.clientWidth > 0 &&
          svgRef.current.clientHeight > 0
        ) {
          const svgWidth = svgRef.current.clientWidth
          const svgHeight = svgRef.current.clientHeight

          // Fit the whole image in the viewport (shared formula).
          const scale = computeFitScale(svgWidth, svgHeight, width, height, initialZoom)
          baseScaleRef.current = scale

          // Center the image in available space
          const translateX = svgWidth / 2
          const translateY = svgHeight / 2

          const newTransform = d3.zoomIdentity
            .translate(translateX, translateY)
            .scale(scale)

          setTransform(newTransform)
          transformRef.current = newTransform
          isFitReadyRef.current = true
          setIsFitReady(true)
        }
      }
    }
  }, [backgroundImage, initialZoom])

  // Setup zoom behavior and update the transform directly on the SVG group.
  useEffect(() => {
    if (!svgRef.current || !imageSize || !groupRef.current) return

    const svg = d3.select(svgRef.current)
    const group = d3.select(groupRef.current)

    // Calculate minimum scale needed to fit entire image in viewport
    // This ensures user can always zoom out to see the full image
    const svgWidth = svgRef.current.clientWidth
    const svgHeight = svgRef.current.clientHeight
    // FS-562: deep-zoom map — 100% == the fit baseline and zoom-out is the
    // uniform linear ratio down to ZOOM_MIN_PERCENT. Above 100% the range is
    // re-anchored so the 200% cap reaches `fitZoomMaxScale` (2× native pixels):
    // deep enough to read fine text on a large drawing. The previous cap of 2×
    // *fit* never even reached native resolution for big P&IDs (see
    // `deepZoomPercent` in constants/zoom.ts).
    const fitBaseline = computeFitScale(svgWidth, svgHeight, imageSize.width, imageSize.height, initialZoom)
    const minScaleToFitImage = fitBaseline * (ZOOM_MIN_PERCENT / 100)
    const maxScaleForZoom = fitZoomMaxScale(fitBaseline)

    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([minScaleToFitImage, maxScaleForZoom])
      .filter((event) => {
        // Never start a zoom/pan from a connection-marker drag (the draggable
        // dots carry data-marker-drag=true).
        const target = event.target as Element | null
        if (target && target.closest && target.closest('[data-marker-drag="true"]')) {
          return false
        }
        if (draggingMarkerIdRef.current) return false
        // ENG-2588: wheel/pinch always zoom (cursor-anchored, every mode); drag
        // pans only with the hand tool, Spacebar, or middle mouse. See zoomGesture.
        const mouseEvent = event as MouseEvent
        const touchEvent = event as TouchEvent
        return shouldD3HandleGesture(
          {
            type: event.type,
            button: typeof mouseEvent.button === 'number' ? mouseEvent.button : 0,
            ctrlKey: Boolean(mouseEvent.ctrlKey),
            touchCount: touchEvent.touches ? touchEvent.touches.length : 0,
          },
          { mode: modeRef.current, spacePanning: spacePanningRef.current },
        )
      })
      .on('zoom', (event) => {
        group.attr('transform', event.transform)
        transformRef.current = event.transform

        // Throttle React state updates to once per animation frame.
        if (!animatingRef.current) {
          animatingRef.current = true
          requestAnimationFrame(() => {
            setTransform(transformRef.current)
            // Drive the readout from the actual transform so it tracks wheel
            // zoom too (not just button clicks). 100% == fit, 200% == max zoom.
            setDisplayZoom(Math.round(deepZoomPercent(transformRef.current.k, baseScaleRef.current)))
            animatingRef.current = false
          })
        }
      })

    zoomRef.current = zoom
    svg.call(zoom)
    svg.call(zoom.transform, transformRef.current)
  }, [imageSize, initialZoom])

  // Recalculate transform when container is resized (e.g., when shape is resized on canvas)
  useEffect(() => {
    if (!svgRef.current || !imageSize || !groupRef.current || !zoomRef.current) return

    const svg = svgRef.current
    const zoom = zoomRef.current

    const resizeObserver = new ResizeObserver(() => {
      const svgWidth = svg.clientWidth
      const svgHeight = svg.clientHeight
      if (!svgWidth || !svgHeight) return

      // Fit the whole image in the viewport (shared formula).
      const scale = computeFitScale(svgWidth, svgHeight, imageSize.width, imageSize.height, initialZoom)

      // Center the image in available space
      const translateX = svgWidth / 2
      const translateY = svgHeight / 2

      const newTransform = d3.zoomIdentity
        .translate(translateX, translateY)
        .scale(scale)

      if (!isFitReadyRef.current) {
        // First valid fit (e.g. img.onload ran before the SVG had layout).
        // Apply instantly and reveal the canvas — never animate from the
        // identity transform that produces the zoomed-line flash.
        d3.select(svg).call(zoom.transform, newTransform)
        isFitReadyRef.current = true
        setIsFitReady(true)
        prevSizeRef.current = { w: svgWidth, h: svgHeight }
        return
      }

      // Subsequent resizes (e.g. the details panel opening in UIv2 shrinks the
      // canvas): preserve the user's current zoom level and keep the point
      // currently at the viewport center centered. Re-fitting to the whole
      // image here was discarding the user's pan/zoom — clicking a symbol
      // opened the side panel, and the resulting resize snapped the canvas
      // back to full-image fit, which read as an unwanted "zoom out" on every
      // symbol click. `newTransform` (the fit-to-view) is intentionally unused
      // on this path.
      const prev = prevSizeRef.current
      prevSizeRef.current = { w: svgWidth, h: svgHeight }

      // Don't disturb an in-flight programmatic zoom (zoomToRectangle): let the
      // symbol pan finish rather than interrupting it from the resize.
      if (programmaticZoomRef.current) return
      if (!prev || (prev.w === svgWidth && prev.h === svgHeight)) return

      const current = transformRef.current
      // Group-space point currently centered in the (old) viewport.
      const centerGroupX = (prev.w / 2 - current.x) / current.k
      const centerGroupY = (prev.h / 2 - current.y) / current.k
      // Re-anchor that point to the center of the new viewport, same scale.
      const preserved = d3.zoomIdentity
        .translate(svgWidth / 2 - current.k * centerGroupX, svgHeight / 2 - current.k * centerGroupY)
        .scale(current.k)

      d3.select(svg)
        .interrupt()
        .transition()
        .duration(200)
        .call(zoom.transform, preserved)
    })

    resizeObserver.observe(svg)
    return () => resizeObserver.disconnect()
  }, [imageSize, initialZoom])

  // ENG-2588: keep d3 zoom attached in EVERY mode. Gesture routing now lives in
  // the zoom .filter (via modeRef/spacePanningRef), so we must not tear zoom off
  // in Select mode — doing that is what killed scroll/pinch-zoom the moment an
  // annotator picked the select tool. Drag-to-pan is still gated to the hand
  // tool / Spacebar / middle mouse by the filter.
  useEffect(() => {
    if (!svgRef.current || !zoomRef.current) return
    d3.select(svgRef.current).call(zoomRef.current)
  }, [mode, isConnectionMode])

  const zoomToRectangle = useCallback(
    (rectangle: Rectangle, targetScale?: number) => {
      if (svgRef.current && zoomRef.current && imageSize) {
        const svgWidth = svgRef.current.clientWidth
        const svgHeight = svgRef.current.clientHeight
        // ENG-2588: bail if the canvas is hidden / not laid out (e.g. parked on
        // another tab) — clientWidth/Height are 0 there and would compute a
        // garbage transform that corrupts the view we keep mounted to preserve.
        if (!svgWidth || !svgHeight) return
        // Compute the node's center in group coordinates.
        const nodeGroupX = (rectangle.x + rectangle.width / 2) - imageSize.width / 2
        const nodeGroupY = (rectangle.y + rectangle.height / 2) - imageSize.height / 2

        // Default: zoom in to *frame* the node — scale so the symbol fills a
        // comfortable fraction of the viewport, then pan it to center.
        //
        // This is an ABSOLUTE target derived from the symbol's own size, not a
        // multiplier on the current zoom. The earlier `currentK * 1.5` heuristic
        // was multiplicative, so search Next/Prev ratcheted the canvas into
        // ever-tighter zoom and stacked d3 transitions. An absolute framing
        // scale is idempotent (re-framing the same symbol is a no-op) and is
        // clamped to the zoom extent, so it can't run away. We also never zoom
        // *out* below the user's current level when focusing a symbol — at most
        // we keep the current scale and just pan.
        // Cross-sheet jumps still pass an explicit targetScale.
        const FRAME_FRACTION = 0.12 // symbol's limiting side fills ~12% of viewport — gentle zoom, keeps context
        const framedScale = Math.min(
          (svgWidth * FRAME_FRACTION) / Math.max(rectangle.width, 1),
          (svgHeight * FRAME_FRACTION) / Math.max(rectangle.height, 1)
        )
        const maxScale = zoomRef.current.scaleExtent()[1]
        const newScale =
          targetScale ?? Math.min(Math.max(framedScale, transformRef.current.k), maxScale)

        const newTranslateX = svgWidth / 2 - newScale * nodeGroupX
        const newTranslateY = svgHeight / 2 - newScale * nodeGroupY
        const newTransform = d3.zoomIdentity
          .translate(newTranslateX, newTranslateY)
          .scale(newScale)

        // Interrupt any in-flight transition before queueing a new one so
        // rapid Next/Prev keypresses don't stack animations.
        // Flag the transition as programmatic so the resize handler leaves it
        // alone (the details-panel-open resize must not stomp the symbol pan).
        programmaticZoomRef.current = true
        d3.select(svgRef.current)
          .interrupt()
          .transition()
          .duration(300)
          .on('end interrupt', () => {
            programmaticZoomRef.current = false
          })
          .call(zoomRef.current.transform, newTransform)
      }
    },
    [imageSize]
  )

  // One +/- click moves the readout by a clean 10pp: step in percent space
  // (snapping an off-grid wheel position toward the click direction, same as
  // useZoom surfaces), then map back to a transform scale. Above 100% the map
  // is geometric, so each click changes the scale by the same factor; scaleTo
  // clamps to the d3 scaleExtent.
  const stepZoom = useCallback((direction: 1 | -1) => {
    if (!svgRef.current || !zoomRef.current) return
    const currentPercent = deepZoomPercent(transformRef.current.k, baseScaleRef.current)
    const target = clampPercent(
      nextStepPercent(currentPercent, direction),
      ZOOM_MIN_PERCENT,
      ZOOM_MAX_PERCENT_VECTOR
    )
    const targetK = percentToDeepZoomScale(target, baseScaleRef.current)
    d3.select(svgRef.current).call(zoomRef.current.scaleTo, targetK)
  }, [])

  const handleZoomIn = useCallback(() => {
    stepZoom(1)
  }, [stepZoom])

  const handleZoomOut = useCallback(() => {
    stepZoom(-1)
  }, [stepZoom])

  // FS-562 type-in: snap to the 10% grid, clamp to the 200% vector cap, map back
  // to a transform scale (geometric above 100%). scaleTo also clamps to the d3
  // scaleExtent.
  const handleSetZoom = useCallback((percent: number) => {
    if (!svgRef.current || !zoomRef.current) return
    const target = clampPercent(snapPercentToStep(percent), ZOOM_MIN_PERCENT, ZOOM_MAX_PERCENT_VECTOR)
    const targetK = percentToDeepZoomScale(target, baseScaleRef.current)
    d3.select(svgRef.current).transition().duration(200).call(zoomRef.current.scaleTo, targetK)
  }, [])

  // ENG-2588: "Fit to page" — recompute the fit-to-view transform (same math as
  // the initial fit) and animate to it. On this surface the fit scale is exactly
  // 100%, so this doubles as "reset to 100%" (lands at 100%, drawing centered).
  // Typing 100 in the readout resets the scale without recentering.
  const handleFit = useCallback(() => {
    if (!svgRef.current || !zoomRef.current || !imageSize) return
    const svg = svgRef.current
    const svgWidth = svg.clientWidth
    const svgHeight = svg.clientHeight
    if (!svgWidth || !svgHeight) return
    const scale = computeFitScale(svgWidth, svgHeight, imageSize.width, imageSize.height, initialZoom)
    // Re-stamp the fit baseline so the readout reads 100% at this fit. The
    // canvas may have resized since load (e.g. the details panel opened), which
    // would otherwise leave baseScaleRef stale and read e.g. 92% at fit (P1).
    baseScaleRef.current = scale
    const fitTransform = d3.zoomIdentity
      .translate(svgWidth / 2, svgHeight / 2)
      .scale(scale)
    programmaticZoomRef.current = true
    d3.select(svg)
      .interrupt()
      .transition()
      .duration(300)
      .on('end interrupt', () => {
        programmaticZoomRef.current = false
      })
      .call(zoomRef.current.transform, fitTransform)
  }, [imageSize, initialZoom])

  // ENG-2588: hold Spacebar to temporarily pan in any mode (Figma/tldraw style),
  // so an annotator can reposition without leaving the Select tool. The zoom
  // filter reads spacePanningRef; the cursor + draw-suppression read the state.
  useEffect(() => {
    const setPanning = (on: boolean) => {
      if (spacePanningRef.current === on) return
      spacePanningRef.current = on
      setSpacePanning(on)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.code !== 'Space') return
      // ENG-2588: ignore Space when the canvas is hidden (parked on another
      // tab) — a display:none element reports clientWidth 0.
      if (!svgRef.current || svgRef.current.clientWidth === 0) return
      // Don't hijack Space while typing in a text field. A focused button/select
      // yields Space to pan only while drawing (Create mode); elsewhere it keeps it.
      const active = document.activeElement
      const focusTarget = active
        ? {
            tagName: active.tagName,
            isContentEditable: (active as HTMLElement).isContentEditable,
            role: active.getAttribute ? active.getAttribute('role') : null,
          }
        : null
      if (!shouldSpaceArmPan(focusTarget, modeRef.current)) return
      e.preventDefault() // stop the page from scrolling on Space
      setPanning(true)
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.code !== 'Space') return
      setPanning(false)
    }
    const onBlur = () => setPanning(false)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
    }
  }, [])

  const handleCanvasClick = useCallback(
    (event: React.PointerEvent) => {
      focusedNodeRef.current = null // Reset focused node when clicking canvas

      if (onClick && svgRef.current) {
        const svgCoordinates = getSvgCoordinates(
          svgRef.current,
          event.clientX,
          event.clientY
        )
        const trueCoordinates = getTrueSvgCoordinates(
          svgRef.current,
          event.clientX,
          event.clientY,
          transformRef.current
        )
        const absoluteCoordinates = svgToRectCoordinates(trueCoordinates, imageSize)
        // In connection mode a click on a symbol is routed to the canvas (the symbol's
        // own handlers defer), so tag the click with the symbol it lands on — that's
        // what lets a draw start on / end on a symbol and link to it. Determine that
        // symbol by HIT-TESTING the click against the symbol boxes, not by trusting the
        // hover: a dropped/stale pointerenter (e.g. moving fast onto the 2nd box) would
        // otherwise leave the click unattributed and the line wouldn't end on it. Prefer
        // the hovered symbol when the click is genuinely inside it (z-order aware), else
        // the smallest box containing the click (most specific when symbols nest).
        let connectionNodeId: string | undefined
        if (isConnectionMode) {
          // A few screen px of slop (converted to image space) keeps the hover
          // forgiving — a click a hair off the box, or on its stroke, still lands —
          // without trusting a hover so stale the box has scrolled out from under a
          // stationary cursor (e.g. a wheel-zoom that fires no pointerleave), which
          // would otherwise link the line to empty space.
          const slop = 8 / (transformRef.current.k || 1)
          const lineSlop = 12 / (transformRef.current.k || 1) // thin stroke → a bit more reach
          // ENG-2583: a connection click can land on a box (bbox), a polygon
          // (traced outline/interior), or another line (near its path), so snap-to
          // works for all three — not just boxes.
          const onSymbol = (r: Rectangle): boolean => {
            if (r.geometry_wkt) return false
            // Polygon test first — a traced shape is hit-testable by its outline even
            // if its bbox is degenerate in one axis (a sliver), so don't gate on w/h.
            if (r.vertices && r.vertices.length >= 3) {
              return (
                pointInPolygon(absoluteCoordinates, r.vertices) ||
                nearestPointOnPath(absoluteCoordinates, r.vertices, true).dist <= slop
              )
            }
            if (!r.width || !r.height) return false
            // A junction is a ~10px dot, so the default slop barely extends past it —
            // give it a more generous catch radius so ending/starting a drawn line ON
            // the junction reliably links (the link is what lets it move with the dot).
            const boxSlop = r.isJunction ? Math.max(slop, JUNCTION_CONNECT_SLOP_PX / (transformRef.current.k || 1)) : slop
            return isPointInBox(absoluteCoordinates, r, boxSlop)
          }
          // Strict containment (no slop) — lets us prefer a symbol the click lands
          // INSIDE over one only caught by its slop margin, so a junction's generous
          // catch radius can't steal a click that falls inside a nearby larger symbol.
          const strictlyOn = (r: Rectangle): boolean => {
            if (r.geometry_wkt) return false
            if (r.vertices && r.vertices.length >= 3) return pointInPolygon(absoluteCoordinates, r.vertices)
            if (!r.width || !r.height) return false
            return isPointInBox(absoluteCoordinates, r, 0)
          }
          const hovered = connectionHoveredId
            ? rectangles.find((r) => r.id === connectionHoveredId)
            : undefined
          if (hovered && onSymbol(hovered)) {
            // The hovered, green-highlighted symbol is the user's target and the click
            // is on it — trust it (z-order aware: the hover already picked the top box).
            connectionNodeId = connectionHoveredId ?? undefined
          } else {
            // No (valid) hover: symbols win over lines (connect to the valve, not the
            // pipe passing through it); among symbols the smallest containing one wins;
            // otherwise the nearest line within reach.
            let bestSymbol: Rectangle | undefined
            let bestStrict = false
            let bestLine: Rectangle | undefined
            let bestLineDist = Infinity
            for (const r of rectangles) {
              if (r.geometry_wkt) {
                if (r.id === connectionLineId) continue // don't end a line on itself
                // Measure against every (MULTI)LINESTRING sub-segment, like the
                // renderer — the single-LINESTRING parser would drop a multi-part pipe.
                // parseLineStringSegments yields [x,y] tuples (NaN-filtered, ≥2 pts).
                let d = Infinity
                for (const seg of parseLineStringSegments(r.geometry_wkt)) {
                  const segPts = seg.map((p) => ({ x: p[0], y: p[1] }))
                  d = Math.min(d, nearestPointOnPath(absoluteCoordinates, segPts, false).dist)
                }
                if (d <= lineSlop && d < bestLineDist) {
                  bestLineDist = d
                  bestLine = r
                }
              } else if (onSymbol(r)) {
                const strict = strictlyOn(r)
                const area = (r.width ?? 0) * (r.height ?? 0)
                const bestArea = (bestSymbol?.width ?? 0) * (bestSymbol?.height ?? 0)
                // A strict (inside) match beats a slop-only one; among equal
                // strictness the smallest containing symbol wins.
                if (!bestSymbol || (strict && !bestStrict) || (strict === bestStrict && area < bestArea)) {
                  bestSymbol = r
                  bestStrict = strict
                }
              }
            }
            connectionNodeId = (bestSymbol ?? bestLine)?.id
          }
        }
        onClick({
          ...(connectionNodeId ? { nodeId: connectionNodeId } : {}),
          svg: { x: svgCoordinates.x, y: svgCoordinates.y },
          screen: { x: event.clientX, y: event.clientY },
          group: { x: trueCoordinates.x, y: trueCoordinates.y },
          absolute: { x: absoluteCoordinates.x, y: absoluteCoordinates.y },
          shiftKey: event.shiftKey,
        })
      }
    },
    [onClick, imageSize, isConnectionMode, connectionHoveredId, connectionLineId, rectangles]
  )

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't fire while the user is interacting with a form control — incl. the
      // <select> dropdowns in the multi-select panel, where Backspace/Delete would
      // otherwise wipe the whole selection (ENG-2624).
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        return
      }

      if (
        (event.key === 'Backspace' || event.key === 'Delete') &&
        selectedRectangles.size > 0 &&
        onRectangleDelete
      ) {
        event.preventDefault() // Prevent browser back navigation on Backspace
        onRectangleDelete({ nodeIds: Array.from(selectedRectangles) })
        setSelectedRectangles(new Set())
      }

      // ESC to clear selection
      if (event.key === 'Escape' && selectedRectangles.size > 0) {
        setSelectedRectangles(new Set())
        onSelectionChange?.(new Set())
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedRectangles, onRectangleDelete])

  // Esc cancels an in-progress polygon (ENG-2582).
  useEffect(() => {
    if (!isPolygonMode) return
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return
      if (e.key === 'Escape' && polygonVertices.length > 0) {
        setPolygonVertices([])
        setPolygonCursor(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isPolygonMode, polygonVertices.length])

  const handleRectangleClick = useCallback(
    (rectangle: Rectangle, event: React.MouseEvent<SVGGElement>) => {
      event.stopPropagation()

      if (mode === ModeType.Select) {
        const newSelected = new Set(selectedRectangles)
        if (newSelected.has(rectangle.id)) {
          newSelected.delete(rectangle.id)
        } else {
          newSelected.add(rectangle.id)
        }
        setSelectedRectangles(newSelected)
        onSelectionChange?.(newSelected)
        // Also call onClick to open the HITL editing panel (DetailsFlyout)
        if (onClick && svgRef.current) {
          const svgCoordinates = getSvgCoordinates(svgRef.current, event.clientX, event.clientY)
          const trueCoordinates = getTrueSvgCoordinates(svgRef.current, event.clientX, event.clientY, transformRef.current)
          const absoluteCoordinates = svgToRectCoordinates(trueCoordinates, imageSize)
          onClick({
            nodeId: rectangle.id,
            svg: { x: svgCoordinates.x, y: svgCoordinates.y },
            screen: { x: event.clientX, y: event.clientY },
            group: { x: trueCoordinates.x, y: trueCoordinates.y },
            absolute: { x: absoluteCoordinates.x, y: absoluteCoordinates.y },
          })
        }
        return
      }
      if (mode === ModeType.Drag) {
        // ENG-2624: Cmd/Ctrl+click builds a multi-selection right in the move tool,
        // so you don't have to switch to the Select tool. A plain click clears any
        // multi-selection and opens the single details panel as before.
        if (event.metaKey || event.ctrlKey) {
          // Toggle the clicked node in the multi-selection, seeding from the open
          // panel node when starting fresh (see computeCmdClickSelection).
          const newSelected = computeCmdClickSelection(selectedRectangles, rectangle.id, panelSelectedId)
          setSelectedRectangles(newSelected)
          onSelectionChange?.(newSelected)
          return
        }
        // A plain click starts fresh: clear any multi-selection, then open the
        // single panel. Both updates batch into one render, so the parent's
        // "size >= 2 closes the panel" effect sees size 0 and keeps the panel open.
        if (selectedRectangles.size > 0) {
          setSelectedRectangles(new Set())
          onSelectionChange?.(new Set())
        }
        // Pan mode - fire onClick to open details panel, skip drag-specific interactions
        if (onClick && svgRef.current) {
          const svgCoordinates = getSvgCoordinates(svgRef.current, event.clientX, event.clientY)
          const trueCoordinates = getTrueSvgCoordinates(svgRef.current, event.clientX, event.clientY, transformRef.current)
          const absoluteCoordinates = svgToRectCoordinates(trueCoordinates, imageSize)
          onClick({
            nodeId: rectangle.id,
            svg: { x: svgCoordinates.x, y: svgCoordinates.y },
            screen: { x: event.clientX, y: event.clientY },
            group: { x: trueCoordinates.x, y: trueCoordinates.y },
            absolute: { x: absoluteCoordinates.x, y: absoluteCoordinates.y },
          })
        }
        return
      }
      if (onClick && svgRef.current) {
        const svgCoordinates = getSvgCoordinates(
          svgRef.current,
          event.clientX,
          event.clientY
        )
        const trueCoordinates = getTrueSvgCoordinates(
          svgRef.current,
          event.clientX,
          event.clientY,
          transformRef.current
        )
        const absoluteCoordinates = svgToRectCoordinates(trueCoordinates, imageSize)
        onClick({
          nodeId: rectangle.id,
          svg: { x: svgCoordinates.x, y: svgCoordinates.y },
          screen: { x: event.clientX, y: event.clientY },
          group: { x: trueCoordinates.x, y: trueCoordinates.y },
          absolute: { x: absoluteCoordinates.x, y: absoluteCoordinates.y },
        })
      }

      // Optionally suppress zoom/focus behavior during special modes (e.g., connection add)
      if (!suppressNodeClickActions) {
        // Don't zoom for connection lines (they don't have a single center point)
        if (!rectangle.geometry_wkt) {
          // Only zoom if we're clicking a different node
          if (focusedNodeRef.current !== rectangle.id) {
            zoomToRectangle(rectangle)
            focusedNodeRef.current = rectangle.id
          }
        }
      }
    },
    [mode, selectedRectangles, panelSelectedId, onClick, zoomToRectangle, suppressNodeClickActions, imageSize, onSelectionChange]
  )

  // Drag event handlers
  const handleRectanglePointerDown = useCallback(
    (rectangle: Rectangle, event: React.PointerEvent) => {
      if (mode !== ModeType.Select || isReadOnly) return

      // Don't allow dragging for connection lines (those with geometry_wkt)
      if (rectangle.geometry_wkt) return

      event.stopPropagation()
      event.preventDefault() // Prevent default to ensure we capture the pointer

      // Set pointer capture to ensure we get all pointer events
      const target = event.currentTarget as Element
      target.setPointerCapture(event.pointerId)

      isDraggingRef.current = false // Reset drag flag

      const state = {
        id: rectangle.id,
        startX: event.clientX,
        startY: event.clientY,
        initialRectX: rectangle.x,
        initialRectY: rectangle.y,
      }
      dragRef.current = state
    },
    [mode, isReadOnly]
  )

  // Resize event handlers — also drives polygon-vertex drag (handle 'vertex:<i>',
  // ENG-2582), so a vertex moves through the identical capture → resizeRef →
  // global pointermove/up → onRectangleUpdate path as a valve resize.
  const handleResizePointerDown = useCallback(
    (rectangle: Rectangle, handle: string, event: React.PointerEvent) => {
      if (!canEditShapeHandles(mode, isReadOnly)) return
      // Connection lines aren't resizable; polygons (vertices) are still allowed.
      if (rectangle.geometry_wkt && !handle.startsWith(VERTEX_HANDLE_PREFIX)) return

      event.stopPropagation()
      event.preventDefault()

      const target = event.currentTarget as Element
      target.setPointerCapture(event.pointerId)

      isResizingRef.current = false // Reset resize flag

      const state: ResizeState = {
        id: rectangle.id,
        handle,
        startX: event.clientX,
        startY: event.clientY,
        initialRect: {
          x: rectangle.x,
          y: rectangle.y,
          width: rectangle.width,
          height: rectangle.height,
        },
        // Polygon vertex drag uses rectangle.vertices; a connection-line vertex drag
        // (ENG-2611) uses its LINESTRING points instead, fed through the same path.
        initialVertices:
          rectangle.geometry_wkt && handle.startsWith(VERTEX_HANDLE_PREFIX)
            ? parseLineString(rectangle.geometry_wkt)
            : rectangle.vertices,
      }
      resizeRef.current = state
    },
    [mode, isReadOnly]
  )

  // Split the edge starting at `afterIndex` and commit the longer ring (ENG-2582).
  const handleVertexInsert = useCallback(
    (rectangle: Rectangle, afterIndex: number) => {
      if (!canEditShapeHandles(mode, isReadOnly) || !rectangle.vertices) return
      const verts = insertVertexAfter(rectangle.vertices, afterIndex)
      onRectangleUpdate?.([{ ...rectangle, vertices: verts, ...bboxOfVertices(verts) }])
    },
    [mode, isReadOnly, onRectangleUpdate]
  )

  // Remove a vertex (alt/right-click). deleteVertexAt keeps a triangle minimum.
  const handleVertexDelete = useCallback(
    (rectangle: Rectangle, index: number) => {
      if (!canEditShapeHandles(mode, isReadOnly) || !rectangle.vertices) return
      const verts = deleteVertexAt(rectangle.vertices, index)
      if (verts === rectangle.vertices) return // at the 3-vertex floor — no-op
      onRectangleUpdate?.([{ ...rectangle, vertices: verts, ...bboxOfVertices(verts) }])
    },
    [mode, isReadOnly, onRectangleUpdate]
  )

  // Track which node we've already zoomed to, so rectangle rebuilds (e.g. from search)
  // don't forcibly re-zoom the user back to the same node.
  const lastZoomedNodeRef = useRef<string | null>(null)
  const crossSheetZoomTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Reset when selectedNodeId changes so we zoom to the new node
  useEffect(() => {
    if (crossSheetZoomTimerRef.current) {
      clearTimeout(crossSheetZoomTimerRef.current)
      crossSheetZoomTimerRef.current = null
    }
    lastZoomedNodeRef.current = null
  }, [selectedNodeId])

  // Hand the zoom actions to a host that draws its own bar, and keep its
  // readout in step with the canvas (wheel zoom included — `displayZoom` is
  // driven from the actual transform).
  useEffect(() => {
    if (!zoomHandleRef) return
    zoomHandleRef.current = {
      zoomIn: handleZoomIn,
      zoomOut: handleZoomOut,
      setPercent: handleSetZoom,
      fit: handleFit,
    }
    return () => {
      zoomHandleRef.current = null
    }
  }, [zoomHandleRef, handleZoomIn, handleZoomOut, handleSetZoom, handleFit])
  useEffect(() => {
    onZoomChange?.(displayZoom)
  }, [onZoomChange, displayZoom])

  // Handle programmatic node selection
  useEffect(() => {
    if (!selectedNodeId || !svgRef.current || !zoomRef.current || !imageSize) return
    if (lastZoomedNodeRef.current === selectedNodeId) return
    // Connection-draw mode sets selectedNodeId as a side-effect of highlighting
    // the anchor symbol — skip the auto-zoom or the view jumps and the user
    // can't see the other end they're trying to connect to.
    if (isConnectionMode) return
    const selectedRectangle = rectangles.find((r) => r.id === selectedNodeId)
    if (!selectedRectangle || selectedRectangle.geometry_wkt) return

    if (isCrossSheetHighlight) {
      // Cross-sheet zoom needs a delay — fit-to-screen init + resize observer
      // fire after imageSize is set and would override an immediate zoom.
      // Set ref immediately to prevent re-scheduling on dep changes.
      // Timer is stored in a persistent ref (not cleaned up by effect return)
      // so it survives rectangle/imageSize changes during the delay.
      lastZoomedNodeRef.current = selectedNodeId
      crossSheetZoomTimerRef.current = setTimeout(() => {
        zoomToRectangle(selectedRectangle, 0.8)
        crossSheetZoomTimerRef.current = null
      }, 500)
      return
    }

    // Normal in-sheet selection: zoom synchronously
    zoomToRectangle(selectedRectangle)
    lastZoomedNodeRef.current = selectedNodeId
  }, [selectedNodeId, rectangles, imageSize, zoomToRectangle, isCrossSheetHighlight, isConnectionMode])

  // FS-523: frame the checked objects so the amber halos are actually in view.
  // Fits the union bounds of the highlighted nodes (zooming out if they span a
  // large area), and re-frames whenever the checked set changes — but not on
  // unrelated rectangle rebuilds.
  const lastReviewSigRef = useRef<string>('')
  useEffect(() => {
    if (!reviewHighlightIds || reviewHighlightIds.size === 0) {
      lastReviewSigRef.current = ''
      return
    }
    if (!svgRef.current || !zoomRef.current || !imageSize) return
    const sig = Array.from(reviewHighlightIds).sort().join(',')
    if (sig === lastReviewSigRef.current) return
    const targets = rectangles.filter((r) => reviewHighlightIds.has(r.id) && !r.geometry_wkt)
    if (targets.length === 0) return
    lastReviewSigRef.current = sig

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const r of targets) {
      minX = Math.min(minX, r.x)
      minY = Math.min(minY, r.y)
      maxX = Math.max(maxX, r.x + r.width)
      maxY = Math.max(maxY, r.y + r.height)
    }
    const boundsW = maxX - minX
    const boundsH = maxY - minY
    const svgW = svgRef.current.clientWidth
    const svgH = svgRef.current.clientHeight
    const FIT_FRACTION = 0.7 // union bounds fill ~70% of the viewport
    const fitScale = Math.min(
      (svgW * FIT_FRACTION) / Math.max(boundsW, 1),
      (svgH * FIT_FRACTION) / Math.max(boundsH, 1),
    )
    const [minScale, maxScale] = zoomRef.current.scaleExtent()
    const targetScale = Math.max(minScale, Math.min(fitScale, maxScale))
    zoomToRectangle(
      { id: '__review_bounds__', x: minX, y: minY, width: boundsW, height: boundsH, color: '', zIndex: 0 },
      targetScale,
    )
  }, [reviewHighlightIds, rectangles, imageSize, zoomToRectangle])

  // Add drag and resize event listeners. Active wherever handles are editable
  // (ENG-2609: any tool mode, so a freshly drawn shape stays vertex-editable
  // after the editor flips to the Drag/pan tool). Outside Select only the
  // resize/vertex handles populate resizeRef — rectangle body-drag stays
  // Select-only via handleRectanglePointerDown — so this commits vertex/corner
  // edits without enabling whole-shape moves outside Select.
  useEffect(() => {
    if (canEditShapeHandles(mode, isReadOnly)) {
      const handleGlobalPointerMove = (event: PointerEvent) => {
        // Handle dragging
        if (dragRef.current && svgRef.current) {
          const { startX, startY, initialRectX, initialRectY } = dragRef.current

          // Convert screen coordinates to SVG coordinates
          const startSvgCoords = getSvgCoordinates(
            svgRef.current,
            startX,
            startY
          )
          const currentSvgCoords = getSvgCoordinates(
            svgRef.current,
            event.clientX,
            event.clientY
          )

          // Calculate the difference in SVG coordinates
          const dx =
            (currentSvgCoords.x - startSvgCoords.x) / transformRef.current.k
          const dy =
            (currentSvgCoords.y - startSvgCoords.y) / transformRef.current.k

          // Check if we've moved enough to consider it a drag
          const distance = Math.sqrt(dx * dx + dy * dy)
          if (distance > MIN_DRAG_DISTANCE_IN_PIXELS) {
            isDraggingRef.current = true
          }

          // Update local temp state only - don't call onRectangleUpdate
          const rect = rectangles.find((r) => r.id === dragRef.current!.id)!
          setTempRectangle({
            ...rect,
            x: initialRectX + dx,
            y: initialRectY + dy,
          })
        }

        // Handle resizing
        if (resizeRef.current && svgRef.current) {
          const { startX, startY, initialRect, handle, initialVertices } = resizeRef.current

          // Convert screen coordinates to SVG coordinates
          const startSvgCoords = getSvgCoordinates(svgRef.current, startX, startY)
          const currentSvgCoords = getSvgCoordinates(
            svgRef.current,
            event.clientX,
            event.clientY
          )

          // Calculate the difference in SVG coordinates
          const dx = (currentSvgCoords.x - startSvgCoords.x) / transformRef.current.k
          const dy = (currentSvgCoords.y - startSvgCoords.y) / transformRef.current.k

          // Check if we've moved enough to consider it a resize
          const distance = Math.sqrt(dx * dx + dy * dy)
          if (distance > MIN_DRAG_DISTANCE_IN_PIXELS) {
            isResizingRef.current = true
          }

          // Polygon vertex drag (ENG-2582): move the one vertex and resync the
          // bbox, then exit — the box-resize math below doesn't apply.
          if (handle.startsWith(VERTEX_HANDLE_PREFIX) && initialVertices) {
            const idx = Number(handle.slice(VERTEX_HANDLE_PREFIX.length))
            const verts = moveVertex(initialVertices, idx, dx, dy)
            const polyRect = rectangles.find((r) => r.id === resizeRef.current!.id)!
            if (polyRect.geometry_wkt) {
              // Connection line (ENG-2611): rewrite its LINESTRING from the moved
              // points so it re-renders as a polyline, not a polygon.
              setTempRectangle({ ...polyRect, geometry_wkt: toLineStringWkt(verts) })
            } else {
              setTempRectangle({ ...polyRect, vertices: verts, ...bboxOfVertices(verts) })
            }
            return
          }

          // Calculate new dimensions based on handle
          let newX = initialRect.x
          let newY = initialRect.y
          let newWidth = initialRect.width
          let newHeight = initialRect.height

          const MIN_SIZE = 10 // Minimum rectangle size

          switch (handle) {
            case 'tl': // Top-left
              newX = initialRect.x + dx
              newY = initialRect.y + dy
              newWidth = initialRect.width - dx
              newHeight = initialRect.height - dy
              break
            case 'tr': // Top-right
              newY = initialRect.y + dy
              newWidth = initialRect.width + dx
              newHeight = initialRect.height - dy
              break
            case 'bl': // Bottom-left
              newX = initialRect.x + dx
              newWidth = initialRect.width - dx
              newHeight = initialRect.height + dy
              break
            case 'br': // Bottom-right
              newWidth = initialRect.width + dx
              newHeight = initialRect.height + dy
              break
            case 't': // Top edge
              newY = initialRect.y + dy
              newHeight = initialRect.height - dy
              break
            case 'r': // Right edge
              newWidth = initialRect.width + dx
              break
            case 'b': // Bottom edge
              newHeight = initialRect.height + dy
              break
            case 'l': // Left edge
              newX = initialRect.x + dx
              newWidth = initialRect.width - dx
              break
          }

          // Enforce minimum size
          if (newWidth < MIN_SIZE) {
            newWidth = MIN_SIZE
            if (handle.includes('l')) {
              newX = initialRect.x + initialRect.width - MIN_SIZE
            }
          }
          if (newHeight < MIN_SIZE) {
            newHeight = MIN_SIZE
            if (handle.includes('t')) {
              newY = initialRect.y + initialRect.height - MIN_SIZE
            }
          }

          // Update local temp state only - don't call onRectangleUpdate
          const rect = rectangles.find((r) => r.id === resizeRef.current!.id)!
          setTempRectangle({
            ...rect,
            x: newX,
            y: newY,
            width: newWidth,
            height: newHeight,
          })
        }
      }

      const handleGlobalPointerUp = (event: PointerEvent) => {
        // Only update the store once when done
        if (tempRectangle) {
          onRectangleUpdate?.([tempRectangle])

          // If this was a box resize, open the details panel — but not for a
          // polygon vertex drag (ENG-2582), which shouldn't pop the panel on
          // every point you nudge.
          if (isResizingRef.current && !resizeRef.current?.handle.startsWith(VERTEX_HANDLE_PREFIX)) {
            // resizeRef is still populated here — it is cleared further down, after this.
            const { x, y, width, height } = tempRectangle
            onRectangleResizeComplete?.(
              tempRectangle.id,
              resizeRef.current?.handle,
              resizeRef.current?.initialRect,
              { x, y, width, height },
            )
          }

          setTempRectangle(null)
        }

        if (dragRef.current) {
          // Release pointer capture
          const target = event.target as Element
          if (target.releasePointerCapture) {
            target.releasePointerCapture(event.pointerId)
          }
        }
        if (resizeRef.current) {
          // Release pointer capture
          const target = event.target as Element
          if (target.releasePointerCapture) {
            target.releasePointerCapture(event.pointerId)
          }
        }
        dragRef.current = null
        resizeRef.current = null
        isDraggingRef.current = false
        isResizingRef.current = false
      }

      window.addEventListener('pointermove', handleGlobalPointerMove)
      window.addEventListener('pointerup', handleGlobalPointerUp)

      return () => {
        window.removeEventListener('pointermove', handleGlobalPointerMove)
        window.removeEventListener('pointerup', handleGlobalPointerUp)
      }
    }
  }, [mode, isReadOnly, rectangles, tempRectangle, onRectangleUpdate])

  // Pre-compute rectangle lookup Map for O(1) edge lookups (instead of O(n) .find())
  const rectangleById = useMemo(() => {
    const map = new Map<string, Rectangle>()
    for (const r of rectangles) {
      map.set(r.id, r)
    }
    return map
  }, [rectangles])

  // Armin (Slack): visually dock a parsed connection line's endpoints onto the objects
  // it joins (`touches_connection` edge), so a backend-recorded connection reads as
  // overlapping — matching "Add Object". Purely visual; stored geometry is untouched.
  // Scope is line→object only: boxes/tees never move (that would distort real coords),
  // we only pull the connecting line's END onto the object it touches.
  const dockedLineEnds = useMemo(() => {
    const result = new Map<string, { start?: Pt; end?: Pt }>()
    if (!edges.length) return result
    // Cache per rectangle — a junction with many pipes appears in many edges, and
    // parsing its WKT once instead of per-edge matters on dense connection graphs.
    const lineCache = new Map<string, boolean>()
    const isLine = (r?: Rectangle) => {
      if (!r) return false
      const cached = lineCache.get(r.id)
      if (cached !== undefined) return cached
      const v = !!r.geometry_wkt && parseLineStringSegments(r.geometry_wkt).length > 0
      lineCache.set(r.id, v)
      return v
    }
    const toDockShape = (r: Rectangle): DockShape => ({
      id: r.id, x: r.x, y: r.y, width: r.width, height: r.height,
      vertices: r.vertices, isJunction: r.isJunction,
    })

    const objectsByLine = new Map<string, Rectangle[]>()
    for (const e of edges) {
      if (e.relation !== 'touches_connection') continue
      const a = rectangleById.get(e.fromNodeId)
      const b = rectangleById.get(e.toNodeId)
      if (!a || !b) continue
      const aLine = isLine(a)
      const bLine = isLine(b)
      if (aLine === bLine) continue // only line↔object (skip line↔line and box↔box)
      const line = aLine ? a : b
      const obj = aLine ? b : a
      const arr = objectsByLine.get(line.id)
      if (arr) arr.push(obj)
      else objectsByLine.set(line.id, [obj])
    }

    for (const [lineId, objs] of objectsByLine) {
      const line = rectangleById.get(lineId)
      if (!line?.geometry_wkt) continue
      const segs = parseLineStringSegments(line.geometry_wkt)
      if (!segs.length) continue
      const first = segs[0][0]
      const lastSeg = segs[segs.length - 1]
      const last = lastSeg[lastSeg.length - 1]
      if (!first || !last) continue
      const ends: Pt[] = [{ x: first[0], y: first[1] }, { x: last[0], y: last[1] }]
      const docked = dockConnectionEndpoints(ends, objs.map(toDockShape))
      if (docked === ends) continue
      // Only record the end(s) that actually moved — dockConnectionEndpoints keeps the
      // original point reference for an undocked end, so a strict !== identifies changes.
      const entry: { start?: Pt; end?: Pt } = {}
      if (docked[0] !== ends[0]) entry.start = docked[0]
      if (docked[1] !== ends[1]) entry.end = docked[1]
      result.set(lineId, entry)
    }
    return result
  }, [edges, rectangleById])

  const renderedRectangles = useMemo(() => {
    // Use tempRectangle if it exists for the rectangle being dragged/resized
    const rectsToRender = rectangles.map(r =>
      tempRectangle && r.id === tempRectangle.id ? tempRectangle : r
    )

    return [...rectsToRender]
      .sort((a, b) => {
        // First sort by zIndex
        if (a.zIndex !== b.zIndex) {
          return a.zIndex - b.zIndex
        }
        // For same zIndex, sort by size (smaller rectangles on top)
        // This ensures inner/nested tags are selectable
        const aSize = a.width * a.height
        const bSize = b.width * b.height
        return bSize - aSize // Larger rectangles first (rendered below)
      })
      .map((rectangle) => {
        const node = nodes?.get(rectangle.id)
        const isDrawingConnector =
          node?.type === 'symbol' &&
          (() => {
            const n = node as { category?: string; symbol_type?: string; name?: string }
            const cat = (n.category ?? '').toLowerCase()
            const typeStr = (n.symbol_type ?? '').toLowerCase()
            const nameStr = (n.name ?? '').toLowerCase()
            return (
              cat === 'drawing_to_drawing_connector' ||
              typeStr.includes('drawing_to_drawing_connector') ||
              nameStr.includes('drawing_to_drawing_connector')
            )
          })()
        const connectorLabel = isDrawingConnector ? ((node as { label?: string }).label ?? '').trim() : ''
        // Backend-paired portal (connected_to_graph_id) — clicking navigates
        // straight to the linked sheet, so it earns the stronger affordance.
        const isLinkedConnector =
          isDrawingConnector &&
          !!(node as { extraProperties?: Record<string, string> }).extraProperties
            ?.connected_to_graph_id
        return (
        <g
          key={rectangle.id}
          // A per-shape opacity override (Review layer mutes non-flagged
          // detections) wins; otherwise FS-623 focus mode fades every non-matched
          // shape so the spotlighted matches read alone (when a set is active).
          opacity={
            rectangle.opacity ??
            (dimUnmatched && reviewHighlightIds && reviewHighlightIds.size > 0 && !reviewHighlightIds.has(rectangle.id)
              ? 0.1
              : undefined)
          }
          className={`group ${
            selectedRectangles.has(rectangle.id) ? 'selected' : ''
          } ${
            rectangle.geometry_wkt
              ? ''
              : mode === ModeType.Select && !isReadOnly
              ? 'cursor-move'
              : 'cursor-pointer'
          } ${isDrawingConnector ? 'cursor-pointer' : ''}`}
          onClick={(e) => {
            // While drawing a polygon (ENG-2582) a click on an existing node must
            // drop a vertex, not select the node — let it fall through to the
            // canvas (don't stop propagation or open the panel).
            if (isPolygonMode) return
            // Same for connection draw: the canvas pointer-down handles the click
            // (and tags it with this symbol via connectionHoveredId). Returning here
            // avoids a second onClick that would double-add / mis-finish the line.
            if (isConnectionMode) return
            if (isPointMode) return // let the canvas drop a junction point
            if (mode === ModeType.Drag) {
              // In pan mode, still allow clicking nodes to open the details panel
              e.stopPropagation()
              if (!isDraggingRef.current) {
                handleRectangleClick(rectangle, e)
              }
              return
            }
            e.stopPropagation()
            // Handle clicks in all modes except when dragging
            if (!isDraggingRef.current) {
              handleRectangleClick(rectangle, e)
            }
          }}
          onPointerDown={(e) => {
            if (spacePanningRef.current || e.button === 1) return // pan gesture - let d3 zoom handle
            if (isPolygonMode) return // let the canvas place the polygon vertex
            if (isConnectionMode) return // let canvas handle
            if (isPointMode) return // let the canvas drop a junction point
            if (mode === ModeType.Drag) return // Pan mode - let d3 zoom handle
            // Create/draw mode: let the pointer fall through to the canvas so a new
            // object can be drawn INSIDE/over an existing box (whole-shape body-drag
            // is Select-only). Without this the box swallows the draw's pointer-down.
            if (mode === ModeType.Create) return
            e.stopPropagation()
            // Polygons (ENG-2582) edit via their vertex handles, not a bbox body
            // drag — a whole-shape drag would move the box but not the traced
            // vertices, desyncing the two.
            const isPolygon = !!(rectangle.vertices && rectangle.vertices.length >= 3)
            if (mode === ModeType.Select && !isReadOnly && !rectangle.geometry_wkt && !isPolygon) {
              handleRectanglePointerDown(rectangle, e)
            }
          }}
        >
          {rectangle.vertices && rectangle.vertices.length >= 3 ? (
            // Freeform polygon outline (ENG-2582) — a traced shape instead of a
            // bounding box. Same near-transparent fill for hit-testing and stroke
            // styling as the rectangle path below.
            (() => {
              const polyPoints = rectangle.vertices
                .map((v) => `${v.x - (imageSize?.width || 0) / 2},${v.y - (imageSize?.height || 0) / 2}`)
                .join(' ')
              return (
                <>
                  {/* FS-623 follow-up: polygon-outline nodes (e.g. dimension arrows,
                      traced shapes) had no filter/review spotlight — every other shape
                      kind (bbox, line, junction) has one, so a matched polygon silently
                      failed to highlight. Mirror the bbox halo: glow fill + dashed ring. */}
                  {reviewHighlightIds?.has(rectangle.id) && (
                    <g style={{ pointerEvents: 'none' }}>
                      <polygon points={polyPoints} fill={SPOTLIGHT_GLOW} stroke="none" />
                      <polygon
                        points={polyPoints}
                        fill="none"
                        stroke={SPOTLIGHT_RING}
                        strokeWidth={4}
                        strokeDasharray="8 5"
                        strokeLinejoin="round"
                      />
                    </g>
                  )}
                  <polygon
                    points={polyPoints}
                    fill="rgba(0, 0, 0, 0.01)"
                    stroke={
                      selectedRectangles.has(rectangle.id) || selectedNodeId === rectangle.id ? colors.orange[500] : rectangle.color
                    }
                    strokeWidth={8}
                    strokeLinejoin="round"
                    strokeDasharray={rectangle.isManual ? MANUAL_STROKE_DASHARRAY : undefined}
                    data-node-id={rectangle.id}
                    data-testid={`polygon-${rectangle.id}`}
                    style={{
                      // No 'move' cursor: a polygon body isn't draggable (it's edited
                      // via its vertex handles), so don't imply a whole-shape drag.
                      pointerEvents: 'all',
                      cursor: (isConnectionMode || mode === ModeType.Create) ? 'crosshair' : 'pointer',
                    }}
                    // ENG-2583: highlight a polygon as a connection target too, so a
                    // drawn line can snap to / link onto it like a box symbol.
                    onPointerEnter={() => {
                      if (isConnectionMode && onConnectionHover) onConnectionHover(rectangle.id)
                    }}
                    onPointerLeave={() => {
                      if (isConnectionMode && onConnectionHover) onConnectionHover(null)
                    }}
                  />
                </>
              )
            })()
          ) : rectangle.geometry_wkt && parseLineStringSegments(rectangle.geometry_wkt).length > 0 ? (
            // Render connection as one polyline per (MULTI)LINESTRING sub-segment.
            // A single polyline across all sub-segments would bridge disjoint
            // pieces with a phantom corner-to-corner diagonal, and the old
            // LINESTRING-only parser silently dropped every sub-segment after the
            // first — making genuinely multi-part lines vanish (VISN-214). Render
            // each sub-segment separately so multi-part lines draw correctly.
            (() => {
              const segments = parseLineStringSegments(rectangle.geometry_wkt)
              // Dock the line's ends onto the objects it connects to (visual only).
              const docked = dockedLineEnds.get(rectangle.id)
              if (docked && segments.length) {
                if (docked.start) segments[0] = [[docked.start.x, docked.start.y], ...segments[0].slice(1)]
                if (docked.end) {
                  const li = segments.length - 1
                  segments[li] = [...segments[li].slice(0, -1), [docked.end.x, docked.end.y]]
                }
              }
              const stroke =
                selectedRectangles.has(rectangle.id) || selectedNodeId === rectangle.id
                  ? colors.orange[500]
                  : rectangle.color
              return (
                <>
                  {segments.map((segment, i) => {
                    const pointsString = segment
                      .map(
                        ([x, y]) =>
                          `${x - (imageSize?.width || 0) / 2},${y - (imageSize?.height || 0) / 2}`
                      )
                      .join(' ')
                    return (
                      <g key={i}>
                        {/* FS-623: neon-magenta glow behind a filter/review-matched line
                            (lines have no bbox halo, so this is their spotlight). */}
                        {reviewHighlightIds?.has(rectangle.id) && (
                          <polyline
                            points={pointsString}
                            fill="none"
                            stroke={SPOTLIGHT_RING}
                            strokeWidth={18}
                            strokeOpacity={0.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ pointerEvents: 'none' }}
                          />
                        )}
                        <polyline
                        points={pointsString}
                        fill="none"
                        stroke={stroke}
                        strokeWidth={8}
                        strokeDasharray={rectangle.isManual ? MANUAL_STROKE_DASHARRAY : undefined}
                        data-node-id={rectangle.id}
                        data-testid={i === 0 ? `connection-${rectangle.id}` : `connection-${rectangle.id}-${i}`}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          // While DRAWING a line (connection mode), the line must not
                          // capture the pointer — otherwise its 8px stroke sits over the
                          // target symbol and steals the hover/click, so the draw can't
                          // reliably end on that symbol. Outside connection mode the
                          // stroke is clickable so an existing line can be selected.
                          pointerEvents: isConnectionMode
                            ? 'none'
                            : mode === ModeType.Select || mode === ModeType.Drag
                              ? 'stroke'
                              : 'none',
                          cursor: (isConnectionMode || mode === ModeType.Create) ? 'crosshair' : 'pointer'
                        }}
                      />
                      </g>
                    )
                  })}
                </>
              )
            })()
          ) : rectangle.isJunction ? (
            // T / junction (ENG-2613): a small dot, not a bounding box. Same interactive
            // attributes as the rect so it selects, links, and is a snap target — and the
            // same review (amber) + connection-hover (green) halos so it reads as a drop
            // target like any other node, just rendered as concentric rings around the dot.
            (() => {
              const jcx = rectangle.x + rectangle.width / 2 - (imageSize?.width || 0) / 2
              const jcy = rectangle.y + rectangle.height / 2 - (imageSize?.height || 0) / 2
              const jr = Math.max(4, Math.min(rectangle.width, rectangle.height) / 2)
              return (
                <>
                  {reviewHighlightIds?.has(rectangle.id) && (
                    <g style={{ pointerEvents: 'none' }}>
                      <circle cx={jcx} cy={jcy} r={jr + 9} fill={SPOTLIGHT_GLOW} stroke="none" />
                      <circle cx={jcx} cy={jcy} r={jr + 5} fill="none" stroke={SPOTLIGHT_RING} strokeWidth={3} strokeDasharray="6 4" />
                    </g>
                  )}
                  {isConnectionMode && connectionHoveredId === rectangle.id && (
                    <g style={{ pointerEvents: 'none' }}>
                      <circle cx={jcx} cy={jcy} r={jr + 9} fill="rgba(34, 197, 94, 0.18)" stroke="none" />
                      <circle cx={jcx} cy={jcy} r={jr + 5} fill="none" stroke={colors.green[500]} strokeWidth={3} strokeDasharray="6 4" />
                    </g>
                  )}
                  <circle
                    cx={jcx}
                    cy={jcy}
                    r={jr}
                    fill="rgba(0, 0, 0, 0.01)"
                    stroke={selectedRectangles.has(rectangle.id) || selectedNodeId === rectangle.id ? colors.orange[500] : rectangle.color}
                    strokeWidth={3}
                    data-node-id={rectangle.id}
                    data-testid={`junction-${rectangle.id}`}
                    style={{
                      pointerEvents: 'all',
                      cursor: (isConnectionMode || mode === ModeType.Create) ? 'crosshair' : (mode === ModeType.Select && !isReadOnly) ? 'move' : 'pointer',
                    }}
                    onPointerEnter={() => {
                      if (isConnectionMode && onConnectionHover) onConnectionHover(rectangle.id)
                    }}
                    onPointerLeave={() => {
                      if (isConnectionMode && onConnectionHover) onConnectionHover(null)
                    }}
                  />
                </>
              )
            })()
          ) : (
            // Render regular rectangle/bounding box
            <>
              {/* Review halo (FS-523) — amber glow + dashed ring on objects
                  under review in the Pending panel, so the selection is an
                  unmistakable visual hint on the drawing. */}
              {reviewHighlightIds?.has(rectangle.id) && (
                <g style={{ pointerEvents: 'none' }}>
                  <rect
                    x={rectangle.x - (imageSize?.width || 0) / 2 - 10}
                    y={rectangle.y - (imageSize?.height || 0) / 2 - 10}
                    width={rectangle.width + 20}
                    height={rectangle.height + 20}
                    fill={SPOTLIGHT_GLOW}
                    stroke="none"
                    rx={10}
                    ry={10}
                  />
                  <rect
                    x={rectangle.x - (imageSize?.width || 0) / 2 - 4}
                    y={rectangle.y - (imageSize?.height || 0) / 2 - 4}
                    width={rectangle.width + 8}
                    height={rectangle.height + 8}
                    fill="none"
                    stroke={SPOTLIGHT_RING}
                    strokeWidth={4}
                    strokeDasharray="8 5"
                    rx={6}
                    ry={6}
                  />
                </g>
              )}
              {/* Hover halo — shown while drawing a connection. Bright green
                  glow + solid thick ring makes the drop target unmistakable. */}
              {isConnectionMode && connectionHoveredId === rectangle.id && (
                <g style={{ pointerEvents: 'none' }}>
                  {/* Outer soft glow */}
                  <rect
                    x={rectangle.x - (imageSize?.width || 0) / 2 - 10}
                    y={rectangle.y - (imageSize?.height || 0) / 2 - 10}
                    width={rectangle.width + 20}
                    height={rectangle.height + 20}
                    fill="rgba(34, 197, 94, 0.18)"
                    stroke="none"
                    rx={10}
                    ry={10}
                  />
                  {/* Inner crisp ring */}
                  <rect
                    x={rectangle.x - (imageSize?.width || 0) / 2 - 4}
                    y={rectangle.y - (imageSize?.height || 0) / 2 - 4}
                    width={rectangle.width + 8}
                    height={rectangle.height + 8}
                    fill="none"
                    stroke={colors.green[500]}
                    strokeWidth={4}
                    strokeDasharray="8 5"
                    rx={6}
                    ry={6}
                  />
                </g>
              )}
              <rect
                x={rectangle.x - (imageSize?.width || 0) / 2}
                y={rectangle.y - (imageSize?.height || 0) / 2}
                width={rectangle.width}
                height={rectangle.height}
                fill="rgba(0, 0, 0, 0.01)"
                stroke={
                  selectedRectangles.has(rectangle.id) || selectedNodeId === rectangle.id ? colors.orange[500] : rectangle.color
                }
                strokeWidth={8}
                strokeDasharray={rectangle.isManual ? MANUAL_STROKE_DASHARRAY : undefined}
                rx={5}
                ry={5}
                data-node-id={rectangle.id}
                data-testid={`rectangle-${rectangle.id}`}
                className={`${
                  dragRef.current?.id === rectangle.id ? 'opacity-90' : ''
                }`}
                style={{
                  pointerEvents: 'all',
                  cursor: (isConnectionMode || mode === ModeType.Create) ? 'crosshair' : (mode === ModeType.Select && !isReadOnly) ? 'move' : 'pointer'
                }}
                onPointerEnter={() => {
                  if (isConnectionMode && onConnectionHover) onConnectionHover(rectangle.id)
                }}
                onPointerLeave={() => {
                  if (isConnectionMode && onConnectionHover) onConnectionHover(null)
                }}
              />
              {/* Portal ring — D2D connector. Linked portals (backend-paired)
                  get a solid blue ring + corner arrow badge; unlinked ones a
                  dashed amber ring. */}
              {isDrawingConnector && (
                <g style={{ pointerEvents: 'none' }}>
                  <rect
                    x={rectangle.x - (imageSize?.width || 0) / 2 - 4}
                    y={rectangle.y - (imageSize?.height || 0) / 2 - 4}
                    width={rectangle.width + 8}
                    height={rectangle.height + 8}
                    fill="none"
                    stroke={isLinkedConnector ? colors.blue[600] : colors.amber[500]}
                    strokeWidth={4}
                    strokeDasharray={isLinkedConnector ? undefined : '8 5'}
                    rx={6}
                    ry={6}
                    data-testid={`portal-ring-${rectangle.id}`}
                  />
                  {isLinkedConnector && (
                    <g
                      transform={`translate(${rectangle.x - (imageSize?.width || 0) / 2 + rectangle.width + 4}, ${rectangle.y - (imageSize?.height || 0) / 2 - 4})`}
                      data-testid={`portal-badge-${rectangle.id}`}
                    >
                      <circle r={14} fill={colors.blue[600]} />
                      <path
                        d="M -4 4 L 4 -4 M 4 -4 h -6 M 4 -4 v 6"
                        stroke="white"
                        strokeWidth={2.5}
                        fill="none"
                        strokeLinecap="round"
                      />
                    </g>
                  )}
                </g>
              )}
            </>
          )}
          {isDrawingConnector && connectorLabel && (
            <title>Go to drawing: {connectorLabel}</title>
          )}
        </g>
      )
      })
  }, [
    rectangles,
    tempRectangle,
    imageSize,
    selectedRectangles,
    selectedNodeId,
    mode,
    isReadOnly,
    handleRectangleClick,
    handleRectanglePointerDown,
    isConnectionMode,
    connectionHoveredId,
    onConnectionHover,
    nodes,
    reviewHighlightIds,
    // Note: transform intentionally excluded — only resize handles need it (see renderedHandles)
  ])

  // Resize handles rendered separately so they can depend on transform
  // without causing all 100+ rectangles to re-render on every zoom/pan
  const renderedHandles = useMemo(() => {
    // ENG-2611: a selected connection line shows draggable vertex handles too, so
    // don't blanket-exclude connection mode — only the in-progress (unselected) line
    // lacks handles, since handles render only for selected ids below.
    if (!canEditShapeHandles(mode, isReadOnly) || !imageSize) return null

    // Get the active rect (considering temp drag/resize state)
    const getRect = (id: string) => {
      if (tempRectangle && tempRectangle.id === id) return tempRectangle
      return rectangleById.get(id)
    }

    // Collect IDs that need handles
    const handleIds = new Set<string>(selectedRectangles)
    if (selectedNodeId) handleIds.add(selectedNodeId)

    const handleSize = 6 / transformRef.current.k
    const handleStroke = 2 / transformRef.current.k

    return Array.from(handleIds).map(id => {
      const rectangle = getRect(id)
      if (!rectangle) return null

      const offsetX = -(imageSize.width || 0) / 2
      const offsetY = -(imageSize.height || 0) / 2

      // Connection line (ENG-2611): a draggable handle on each LINESTRING point —
      // both endpoints and every 90° corner — so a drawn line can be nudged onto the
      // pipe in place instead of delete-and-redraw. Dragged through the same resizeRef
      // path as a polygon vertex (handle 'vertex:<i>'); the move rewrites the WKT.
      if (rectangle.geometry_wkt) {
        const pts = parseLineString(rectangle.geometry_wkt)
        if (pts.length < 2) return null
        return (
          <g key={`lhandles-${rectangle.id}`}>
            {pts.map((p, i) => (
              <circle
                key={`lv-${i}`}
                cx={p.x + offsetX}
                cy={p.y + offsetY}
                r={handleSize}
                fill="white"
                stroke={colors.orange[500]}
                strokeWidth={handleStroke}
                style={{ cursor: 'move', pointerEvents: 'all' }}
                onPointerDown={(e) => handleResizePointerDown(rectangle, `${VERTEX_HANDLE_PREFIX}${i}`, e)}
                data-testid={`line-vertex-${i}-${rectangle.id}`}
              />
            ))}
          </g>
        )
      }

      // Polygon nodes (ENG-2582): one draggable handle per vertex — dragged via
      // the same resizeRef path as a valve resize (handle 'vertex:<i>'); alt or
      // right-click deletes. A smaller "+" handle at each edge midpoint inserts.
      if (rectangle.vertices && rectangle.vertices.length >= 3) {
        const verts = rectangle.vertices
        return (
          <g key={`vhandles-${rectangle.id}`}>
            {verts.map((v, i) => {
              const next = verts[(i + 1) % verts.length]
              return (
                <circle
                  key={`add-${i}`}
                  cx={(v.x + next.x) / 2 + offsetX}
                  cy={(v.y + next.y) / 2 + offsetY}
                  r={handleSize * 0.7}
                  fill={colors.orange[500]}
                  fillOpacity={0.4}
                  stroke="white"
                  strokeWidth={handleStroke}
                  style={{ cursor: 'copy', pointerEvents: 'all' }}
                  onPointerDown={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    handleVertexInsert(rectangle, i)
                  }}
                  data-testid={`vertex-add-${i}-${rectangle.id}`}
                />
              )
            })}
            {verts.map((v, i) => (
              <circle
                key={`v-${i}`}
                cx={v.x + offsetX}
                cy={v.y + offsetY}
                r={handleSize}
                fill="white"
                stroke={colors.orange[500]}
                strokeWidth={handleStroke}
                style={{ cursor: 'move', pointerEvents: 'all' }}
                onPointerDown={(e) => {
                  if (e.altKey || e.button === 2) {
                    e.stopPropagation()
                    e.preventDefault()
                    handleVertexDelete(rectangle, i)
                    return
                  }
                  handleResizePointerDown(rectangle, `${VERTEX_HANDLE_PREFIX}${i}`, e)
                }}
                onContextMenu={(e) => e.preventDefault()}
                data-testid={`vertex-handle-${i}-${rectangle.id}`}
              />
            ))}
          </g>
        )
      }

      if (rectangle.geometry_wkt) return null

      const allHandles = [
        // Corner handles
        { handle: 'tl', x: rectangle.x + offsetX, y: rectangle.y + offsetY, cursor: 'nwse-resize' },
        { handle: 'tr', x: rectangle.x + offsetX + rectangle.width, y: rectangle.y + offsetY, cursor: 'nesw-resize' },
        { handle: 'bl', x: rectangle.x + offsetX, y: rectangle.y + offsetY + rectangle.height, cursor: 'nesw-resize' },
        { handle: 'br', x: rectangle.x + offsetX + rectangle.width, y: rectangle.y + offsetY + rectangle.height, cursor: 'nwse-resize' },
        // Edge handles
        { handle: 't', x: rectangle.x + offsetX + rectangle.width / 2, y: rectangle.y + offsetY, cursor: 'ns-resize' },
        { handle: 'r', x: rectangle.x + offsetX + rectangle.width, y: rectangle.y + offsetY + rectangle.height / 2, cursor: 'ew-resize' },
        { handle: 'b', x: rectangle.x + offsetX + rectangle.width / 2, y: rectangle.y + offsetY + rectangle.height, cursor: 'ns-resize' },
        { handle: 'l', x: rectangle.x + offsetX, y: rectangle.y + offsetY + rectangle.height / 2, cursor: 'ew-resize' },
      ]

      return (
        <g key={`handles-${rectangle.id}`}>
          {allHandles.map(({ handle, x, y, cursor }) => (
            <circle
              key={handle}
              cx={x}
              cy={y}
              r={handleSize}
              fill="white"
              stroke={colors.orange[500]}
              strokeWidth={handleStroke}
              style={{ cursor, pointerEvents: 'all' }}
              onPointerDown={(e) => handleResizePointerDown(rectangle, handle, e)}
              data-testid={`resize-handle-${handle}-${rectangle.id}`}
            />
          ))}
        </g>
      )
    })
  }, [
    rectangleById,
    tempRectangle,
    selectedRectangles,
    selectedNodeId,
    mode,
    isReadOnly,
    isConnectionMode,
    imageSize,
    transform, // Only handles re-render on zoom — not all rectangles
    handleResizePointerDown,
    handleVertexInsert,
    handleVertexDelete,
  ])

  // Finish the in-progress polygon: drop any trailing near-duplicate vertex
  // (a double-click's second press lands on the first) and emit it.
  const closePolygon = () => {
    if (polygonVertices.length >= 3 && onPolygonAdd) {
      const cleaned = [...polygonVertices]
      while (cleaned.length >= 3) {
        const a = cleaned[cleaned.length - 1]
        const b = cleaned[cleaned.length - 2]
        if (Math.hypot(a.x - b.x, a.y - b.y) < 2) cleaned.pop()
        else break
      }
      onPolygonAdd(cleaned)
    }
    setPolygonVertices([])
    setPolygonCursor(null)
  }

  // Clear any half-drawn polygon when leaving polygon mode.
  useEffect(() => {
    if (!isPolygonMode) {
      setPolygonVertices([])
      setPolygonCursor(null)
    }
  }, [isPolygonMode])

  const handleCanvasPointerDown = (event: React.PointerEvent) => {
    // ENG-2588: a pan gesture (hand tool / Spacebar / middle mouse) begins a
    // pan — let d3 handle it. It must not start a draw/rubber-band, and must not
    // be treated as a deselecting canvas click: panning with a node selected was
    // firing the empty-canvas click that closed the Node Details panel mid-pan.
    if (isCanvasPanPress({ mode, button: event.button, spacePanning: spacePanningRef.current })) {
      return
    }
    // Don't handle canvas events if we're dragging a rectangle
    if (dragRef.current) return

    // Polygon draw (ENG-2582): each click drops a vertex; clicking back near the
    // first vertex (>=3) closes the shape. No rubber-band rectangle here.
    if (isPolygonMode) {
      if (svgRef.current && imageSize) {
        const truePt = getTrueSvgCoordinates(svgRef.current, event.clientX, event.clientY, transform)
        const abs = svgToRectCoordinates(truePt, imageSize)
        if (polygonVertices.length >= 3) {
          const first = polygonVertices[0]
          const closeAbs = 14 / (transformRef.current.k || 1)
          if (Math.hypot(abs.x - first.x, abs.y - first.y) <= closeAbs) {
            closePolygon()
            return
          }
        }
        setPolygonVertices((prev) => [...prev, abs])
      }
      return
    }

    // Point/junction draw (ENG-2613): a single click drops one small symbol dot
    // centered on the cursor (no rubber-band rectangle).
    if (isPointMode) {
      if (svgRef.current && imageSize && onRectangleAdd) {
        const truePt = getTrueSvgCoordinates(svgRef.current, event.clientX, event.clientY, transform)
        const c = svgToRectCoordinates(truePt, imageSize)
        const s = JUNCTION_POINT_SIZE
        onRectangleAdd({ x: c.x - s / 2, y: c.y - s / 2, width: s, height: s })
      }
      return
    }

    if (disableCreate) {
      handleCanvasClick(event)
      return
    }

    if ((mode === 'select' || mode === 'create') && svgRef.current) {
      event.preventDefault()
      const trueCoordinates = getTrueSvgCoordinates(
        svgRef.current,
        event.clientX,
        event.clientY,
        transform
      )
      setSelectionStart(trueCoordinates)
      setSelectionEnd(trueCoordinates)
    }
    handleCanvasClick(event)
  }

  const handleCanvasPointerMove = (event: React.PointerEvent) => {
    // Track cursor position while dragging a connection marker
    if (draggingMarkerId && svgRef.current && imageSize) {
      const truePt = getTrueSvgCoordinates(svgRef.current, event.clientX, event.clientY, transform)
      setDragCursorPos({
        x: truePt.x + imageSize.width / 2,
        y: truePt.y + imageSize.height / 2,
      })
      return
    }

    // Don't handle canvas events if we're dragging a rectangle
    if (dragRef.current) return

    // Polygon draw: track the cursor so the rubber-band segment follows.
    if (isPolygonMode) {
      if (polygonVertices.length > 0 && svgRef.current && imageSize) {
        const truePt = getTrueSvgCoordinates(svgRef.current, event.clientX, event.clientY, transform)
        setPolygonCursor(svgToRectCoordinates(truePt, imageSize))
      }
      return
    }

    // Update connection preview point when in connection mode
    if (isConnectionMode && connectionStartPointAbs && svgRef.current) {
      const trueCoordinates = getTrueSvgCoordinates(
        svgRef.current,
        event.clientX,
        event.clientY,
        transform
      )
      setConnectionPreview(trueCoordinates)
      setPreviewShiftKey(event.shiftKey)
      return
    }

    if (disableCreate) return

    if (
      (mode === ModeType.Select || mode === ModeType.Create) &&
      selectionStart &&
      svgRef.current
    ) {
      const trueCoordinates = getTrueSvgCoordinates(
        svgRef.current,
        event.clientX,
        event.clientY,
        transform
      )
      setSelectionEnd(trueCoordinates)
    }
  }

  const handleCanvasPointerUp = () => {
    // If dragging a connection marker, snap to nearest candidate (if close enough)
    if (draggingMarkerId && connectionMarkers && dragCursorPos) {
      const markerRadius = imageSize
        ? Math.max(imageSize.width, imageSize.height) * 0.006
        : 10
      const snapThreshold = markerRadius * 3
      let nearest: { x: number; y: number; lineId: string; symbolId: string | null } | null = null
      let bestDist = Infinity
      for (const c of connectionMarkers.candidatePoints) {
        const d = Math.hypot(c.x - dragCursorPos.x, c.y - dragCursorPos.y)
        if (d < bestDist && d <= snapThreshold) {
          bestDist = d
          nearest = c
        }
      }
      if (nearest && onMarkerDrop) {
        onMarkerDrop(draggingMarkerId, nearest)
      }
      setDraggingMarkerId(null)
      setDragCursorPos(null)
      return
    }

    // Don't handle canvas events if we're dragging a rectangle
    if (dragRef.current) return

    // Polygon vertices are committed on pointer-down; nothing to finalize here.
    if (isPolygonMode) return

    if (isConnectionMode) {
      setConnectionPreview(null)
    }

    if (disableCreate) {
      setSelectionStart(null)
      setSelectionEnd(null)
      return
    }

    if (mode === ModeType.Select && selectionStart && selectionEnd) {
      const x1 = Math.min(selectionStart.x, selectionEnd.x)
      const y1 = Math.min(selectionStart.y, selectionEnd.y)
      const x2 = Math.max(selectionStart.x, selectionEnd.x)
      const y2 = Math.max(selectionStart.y, selectionEnd.y)
      const newSelected = new Set<string>()

      for (const rectangle of rectangles) {
        // Handle connection lines (LINESTRING geometry)
        if (rectangle.geometry_wkt && rectangle.geometry_wkt.includes('LINESTRING')) {
          const points = parseLineString(rectangle.geometry_wkt)
          // Check if any point of the line is within the selection box
          const lineIntersects = points.some(point => {
            const px = point.x - (imageSize?.width || 0) / 2
            const py = point.y - (imageSize?.height || 0) / 2
            return px >= x1 && px <= x2 && py >= y1 && py <= y2
          })
          if (lineIntersects) {
            newSelected.add(rectangle.id)
          }
        } else {
          // Handle regular rectangles (symbols, tags, instruments)
          const rectX = rectangle.x - (imageSize?.width || 0) / 2
          const rectY = rectangle.y - (imageSize?.height || 0) / 2
          const rectX2 = rectX + rectangle.width
          const rectY2 = rectY + rectangle.height
          if (rectX < x2 && rectX2 > x1 && rectY < y2 && rectY2 > y1) {
            newSelected.add(rectangle.id)
          }
        }
      }

      setSelectedRectangles(newSelected)
      onSelectionChange?.(newSelected)
    } else if (
      mode === ModeType.Create &&
      selectionStart &&
      selectionEnd &&
      onRectangleAdd
    ) {
      const x = Math.min(selectionStart.x, selectionEnd.x)
      const y = Math.min(selectionStart.y, selectionEnd.y)
      const width = Math.abs(selectionEnd.x - selectionStart.x)
      const height = Math.abs(selectionEnd.y - selectionStart.y)
      const rectCoordinates = svgToRectCoordinates({ x, y }, imageSize)
      if (width > 10 && height > 10) {
        onRectangleAdd({
          x: rectCoordinates.x,
          y: rectCoordinates.y,
          width,
          height,
        })
      }
    }
    setSelectionStart(null)
    setSelectionEnd(null)
  }

  // Review mode hides the parse's links; the text-link layer follows it (VISN-360).
  const shown = layers ? shownLayers(layers, hideAssociationLines) : undefined

  return (
    <div className="w-full h-full">
      <svg
        ref={svgRef}
        className={`w-full h-full absolute top-0 bg-gray-100 dark:bg-dark-accent ${
          spacePanning || mode === ModeType.Drag
            ? 'cursor-grab active:cursor-grabbing'
            : mode === ModeType.Create
            ? 'cursor-crosshair'
            : ''
        }`}
        // Hide until the first fit-to-view transform lands so users never
        // catch the SVG mid-identity-transform (the "zoomed line" flash).
        // visibility:hidden keeps the element in layout so clientWidth /
        // clientHeight measurements still work inside img.onload.
        style={{ visibility: backgroundImage && !isFitReady ? 'hidden' : 'visible' }}
        onPointerDown={(e) => {
          // Only stop propagation when no drag/resize operation is in progress
          // Check both immediate refs (dragRef/resizeRef) and threshold refs (isDraggingRef/isResizingRef)
          // for robust handling of all drag/resize states
          if (!dragRef.current && !resizeRef.current && !isDraggingRef.current && !isResizingRef.current) {
            e.stopPropagation()
          }
          handleCanvasPointerDown(e)
        }}
        onPointerMove={(e) => {
          if (!dragRef.current && !resizeRef.current && !isDraggingRef.current && !isResizingRef.current) {
            e.stopPropagation()
          }
          handleCanvasPointerMove(e)
        }}
        onPointerUp={(e) => {
          if (!dragRef.current && !resizeRef.current && !isDraggingRef.current && !isResizingRef.current) {
            e.stopPropagation()
          }
          handleCanvasPointerUp()
        }}
        onWheel={(e) => {
          e.stopPropagation()
        }}
        onDoubleClick={() => {
          // Polygons close on double-click; lines finish on double-click too. The
          // double-click's two near-coincident clicks drop a duplicate end point —
          // the finish path trims it (trimTrailingDuplicateVertices), so there's no
          // stray tiny segment. Esc/Enter and clicking a symbol also finish a line.
          if (isPolygonMode) closePolygon()
          else if (isConnectionMode) onConnectionFinish?.()
        }}
        onMouseDown={(e) => {
          // ENG-2588: suppress the browser's middle-click autoscroll so the
          // middle mouse button can drive a pan instead.
          if (e.button === 1) e.preventDefault()
        }}
      >
        <title>{t('graph_editor:pnidRenderer.editorTitle', { defaultValue: 'P&ID editor' })}</title>
        <g
          ref={groupRef}
          data-testid="canvas-content"
          transform={
            transform
              ? `translate(${transform.x},${transform.y}) scale(${transform.k})`
              : undefined
          }
        >
          {backgroundImage && imageSize && (
            <image
              ref={imageRef}
              href={backgroundImage}
              width={imageSize.width}
              height={imageSize.height}
              x={-imageSize.width / 2}
              y={-imageSize.height / 2}
              data-testid="background-img"
            />
          )}
          <g className="rectangles">{renderedRectangles}</g>
          <g className="resize-handles">{renderedHandles}</g>
          {/* Render tag-to-connection association lines (hidden in the muted Review
              layer — they're a detected overlay, not review items). */}
          <g className="tag-connection-edges">
            {!hideAssociationLines && !layers?.textLinks && nodes && edges.map((edge) => {
              const fromRect = rectangleById.get(edge.fromNodeId)
              const toRect = rectangleById.get(edge.toNodeId)

              // Skip if either rectangle doesn't exist
              if (!fromRect || !toRect || !imageSize) return null

              // Get the actual node objects to check types
              const fromNode = nodes.get(edge.fromNodeId)
              const toNode = nodes.get(edge.toNodeId)

              if (!fromNode || !toNode) return null

              // Only render if one is a TAG and the other is a CONNECTION
              const fromIsTag = fromNode.type === 'tag'
              const toIsTag = toNode.type === 'tag'
              const fromIsConnection = fromNode.type === 'connection'
              const toIsConnection = toNode.type === 'connection'

              // Skip if not a tag-connection pair
              if (!((fromIsTag && toIsConnection) || (toIsTag && fromIsConnection))) {
                return null
              }

              // Determine which is the tag and which is the connection
              const { tagRect, connectionRect } = fromIsConnection
                ? { tagRect: toRect, connectionRect: fromRect }
                : { tagRect: fromRect, connectionRect: toRect }

              // Check if the tag is a VISUAL endpoint of the connection line
              // (i.e., the polyline was drawn FROM or TO this tag's bounding box)
              // If so, skip rendering the association line to prevent duplicates
              if (connectionRect.geometry_wkt && connectionRect.geometry_wkt.includes('LINESTRING')) {
                // Parse the LINESTRING to get start and end points
                const linePoints = parseLineString(connectionRect.geometry_wkt)
                if (linePoints.length >= 2) {
                  const lineStart = linePoints[0]
                  const lineEnd = linePoints[linePoints.length - 1]

                  // Check if either endpoint of the line lands inside the tag box
                  // OR within a small margin of it, so a line a user drew to dock
                  // at the edge (ending a px or two outside the box) doesn't get a
                  // redundant association line drawn into the box.
                  const m = ASSOC_SUPPRESS_MARGIN_PX
                  const tagBounds = {
                    left: tagRect.x - m,
                    right: tagRect.x + tagRect.width + m,
                    top: tagRect.y - m,
                    bottom: tagRect.y + tagRect.height + m,
                  }

                  const pointInTagBounds = (p: { x: number; y: number }) =>
                    p.x >= tagBounds.left && p.x <= tagBounds.right &&
                    p.y >= tagBounds.top && p.y <= tagBounds.bottom

                  if (pointInTagBounds(lineStart) || pointInTagBounds(lineEnd)) {
                    // The polyline already visually reaches this tag, skip the association line
                    return null
                  }
                }
              }

              // Get the center of the tag rectangle (in absolute coordinates)
              const tagCenterX = tagRect.x + tagRect.width / 2
              const tagCenterY = tagRect.y + tagRect.height / 2
              
              // Convert to SVG coordinates for rendering
              const tagX = tagCenterX - imageSize.width / 2
              const tagY = tagCenterY - imageSize.height / 2

              // For connections without geometry, skip (no point to connect to)
              if (!connectionRect.geometry_wkt) return null

              // Get the connection line points
              const connectionPoints = parseLineString(connectionRect.geometry_wkt)
              if (connectionPoints.length === 0) return null

              // Find the closest point on the connection line to the tag center
              const closestPoint = closestPointOnPolyline(tagCenterX, tagCenterY, connectionPoints)
              if (!closestPoint) return null

              // Convert to SVG coordinates for rendering
              const connectionX = closestPoint.x - imageSize.width / 2
              const connectionY = closestPoint.y - imageSize.height / 2

              return (
                <line
                  key={edge.id || `${edge.fromNodeId}-${edge.toNodeId}`}
                  x1={tagX}
                  y1={tagY}
                  x2={connectionX}
                  y2={connectionY}
                  stroke="#FF0000"
                  strokeWidth={6}
                  strokeLinecap="round"
                  style={{ pointerEvents: 'none' }}
                />
              )
            })}
          </g>
          {shown && nodes && imageSize && (shown.flow || shown.textLinks || shown.lineNumbers) && (
            <EditorLayersOverlay layers={shown} rectangles={rectangles} edges={edges} nodes={nodes} imageSize={imageSize} />
          )}
          {children}
          {/* Auto-detected equipment boundary highlight — rendered with higher
              opacity and thicker stroke so it's visible through the drawn bbox */}
          {detectedBoundary && imageSize && (
            <rect
              x={detectedBoundary.x - imageSize.width / 2}
              y={detectedBoundary.y - imageSize.height / 2}
              width={detectedBoundary.width}
              height={detectedBoundary.height}
              fill="rgba(59, 130, 246, 0.18)"
              stroke="#1d4ed8"
              strokeWidth={Math.max(imageSize.width, imageSize.height) * 0.003}
              strokeDasharray={`${Math.max(imageSize.width, imageSize.height) * 0.008} ${Math.max(imageSize.width, imageSize.height) * 0.004}`}
              style={{ pointerEvents: 'none' }}
            />
          )}
          {/* Connection detection markers — green = connected, red = dangling.
              Each marker is individually draggable. While dragging, candidate
              drop targets (nearby line endpoints from parsing_state) glow cyan. */}
          {connectionMarkers && imageSize && (() => {
            const markerRadius = Math.max(imageSize.width, imageSize.height) * 0.006
            const connectedMarkerRadius = markerRadius * 0.55
            const dotRadius = connectedMarkerRadius * 0.35
            const strokeW = connectedMarkerRadius * 0.25
            const candidateRadius = markerRadius * 0.25
            const snapThreshold = markerRadius * 2
            const MAX_VISIBLE_CANDIDATES = 10
            const visibleCandidateIds = new Set<number>()
            if (draggingMarkerId && dragCursorPos && connectionMarkers.candidatePoints.length) {
              const ranked = connectionMarkers.candidatePoints
                .map((c, i) => ({ i, d: Math.hypot(c.x - dragCursorPos.x, c.y - dragCursorPos.y) }))
                .sort((a, b) => a.d - b.d)
                .slice(0, MAX_VISIBLE_CANDIDATES)
              for (const r of ranked) visibleCandidateIds.add(r.i)
            }

            const startDragForMarker = (markerId: string) => (e: React.PointerEvent) => {
              e.stopPropagation()
              setDraggingMarkerId(markerId)
              if (svgRef.current) {
                const svgPt = getSvgCoordinates(svgRef.current, e.clientX, e.clientY)
                const truePt = {
                  x: (svgPt.x - transform.x) / transform.k,
                  y: (svgPt.y - transform.y) / transform.k,
                }
                setDragCursorPos({
                  x: truePt.x + imageSize.width / 2,
                  y: truePt.y + imageSize.height / 2,
                })
              }
            }

            const renderMarker = (
              pt: { x: number; y: number; markerId: string },
              fill: string,
              stroke: string,
              keyPrefix: string,
            ) => {
              const isDragging = draggingMarkerId === pt.markerId
              const displayX = isDragging && dragCursorPos ? dragCursorPos.x : pt.x
              const displayY = isDragging && dragCursorPos ? dragCursorPos.y : pt.y
              return (
                <g
                  key={`${keyPrefix}-${pt.markerId}`}
                  data-marker-drag="true"
                  style={{ cursor: 'grab', pointerEvents: 'all' }}
                  onPointerDown={startDragForMarker(pt.markerId)}
                >
                  <circle
                    cx={displayX - imageSize.width / 2}
                    cy={displayY - imageSize.height / 2}
                    r={connectedMarkerRadius}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={strokeW}
                  />
                  <circle
                    cx={displayX - imageSize.width / 2}
                    cy={displayY - imageSize.height / 2}
                    r={dotRadius}
                    fill={stroke}
                  />
                </g>
              )
            }

            return (
              <g className="connection-markers">
                {/* Candidate drop targets — visible only while dragging */}
                {draggingMarkerId && connectionMarkers.candidatePoints.map((c, i) => {
                  if (!visibleCandidateIds.has(i)) return null
                  const nearCursor = dragCursorPos
                    ? Math.hypot(c.x - dragCursorPos.x, c.y - dragCursorPos.y) <= snapThreshold
                    : false
                  return (
                    <circle
                      key={`cand-${i}`}
                      cx={c.x - imageSize.width / 2}
                      cy={c.y - imageSize.height / 2}
                      r={nearCursor ? candidateRadius * 1.6 : candidateRadius}
                      fill={nearCursor ? 'rgba(6, 182, 212, 0.5)' : 'rgba(6, 182, 212, 0.25)'}
                      stroke="#06b6d4"
                      strokeWidth={strokeW * 0.7}
                      style={{ pointerEvents: 'none' }}
                    />
                  )
                })}
                {connectionMarkers.connectedPoints.map((pt) =>
                  renderMarker(pt, 'rgba(34, 197, 94, 0.3)', '#22c55e', 'conn'),
                )}
                {connectionMarkers.danglingPoints.map((pt) =>
                  renderMarker(pt, 'rgba(239, 68, 68, 0.3)', '#ef4444', 'dangle'),
                )}
              </g>
            )
          })()}
          {/* Live connection preview — only the PENDING segment (last committed
              point → cursor). Each placed point commits as a real (solid)
              connection as you draw, so the earlier segments already render; the
              dashed rubber-band is just the not-yet-placed segment. Free angle by
              default; Shift orthogonal-snaps the cursor segment. */}
          {isConnectionMode && connectionStartPointAbs && connectionPreview && imageSize && (
            (() => {
              const halfW = imageSize.width / 2
              const halfH = imageSize.height / 2
              const startRel = { x: connectionStartPointAbs.x - halfW, y: connectionStartPointAbs.y - halfH }
              const vertsRel = connectionVertices.map((v) => ({ x: v.x - halfW, y: v.y - halfH }))
              const prev = vertsRel.length > 0 ? vertsRel[vertsRel.length - 1] : startRel
              const cursor = connectionPreview
              const dx = Math.abs(cursor.x - prev.x)
              const dy = Math.abs(cursor.y - prev.y)
              const cursorSnapped = previewShiftKey
                ? (dx >= dy ? { x: cursor.x, y: prev.y } : { x: prev.x, y: cursor.y })
                : cursor
              const pathPts = [prev, cursorSnapped]
              const pointsAttr = pathPts.map((p) => `${p.x},${p.y}`).join(' ')
              return (
                <g style={{ pointerEvents: 'none' }}>
                  <polyline
                    points={pointsAttr}
                    fill="none"
                    stroke={colors.red[500]}
                    strokeWidth={6}
                    strokeDasharray="16 10"
                    strokeLinecap="butt"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                  {/* Anchor dot at the start click — makes it obvious where the line originates
                      (especially important on large equipment where the click might look centered). */}
                  <circle
                    cx={startRel.x}
                    cy={startRel.y}
                    r={6}
                    fill={colors.red[500]}
                    stroke="white"
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                  />
                  {/* Small dots on each committed vertex so the user sees where turns have been placed. */}
                  {connectionVertices.map((v, i) => (
                    <circle
                      key={`vertex-${i}`}
                      cx={v.x - halfW}
                      cy={v.y - halfH}
                      r={4}
                      fill={colors.red[500]}
                      vectorEffect="non-scaling-stroke"
                    />
                  ))}
                </g>
              )
            })()
          )}
          {/* In-progress freeform polygon (ENG-2582): committed vertices + a
              rubber-band segment to the cursor, with a dashed "close" hint back
              to the first vertex once 3+ points are placed. */}
          {isPolygonMode && polygonVertices.length > 0 && imageSize && (() => {
            const halfW = imageSize.width / 2
            const halfH = imageSize.height / 2
            const rel = polygonVertices.map((v) => ({ x: v.x - halfW, y: v.y - halfH }))
            const cursorRel = polygonCursor ? { x: polygonCursor.x - halfW, y: polygonCursor.y - halfH } : null
            const linePts = cursorRel ? [...rel, cursorRel] : rel
            const pointsAttr = linePts.map((p) => `${p.x},${p.y}`).join(' ')
            const first = rel[0]
            const canClose = polygonVertices.length >= 3
            return (
              <g style={{ pointerEvents: 'none' }}>
                <polyline
                  points={pointsAttr}
                  fill="none"
                  stroke={colors.red[500]}
                  strokeWidth={6}
                  strokeDasharray="16 10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
                {canClose && cursorRel && (
                  <line
                    x1={cursorRel.x}
                    y1={cursorRel.y}
                    x2={first.x}
                    y2={first.y}
                    stroke={colors.red[400]}
                    strokeWidth={3}
                    strokeDasharray="6 8"
                    vectorEffect="non-scaling-stroke"
                  />
                )}
                {rel.map((p, i) => (
                  <circle
                    key={`pv-${i}`}
                    cx={p.x}
                    cy={p.y}
                    r={i === 0 && canClose ? 8 : 4}
                    fill={i === 0 && canClose ? 'white' : colors.red[500]}
                    stroke={colors.red[500]}
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
              </g>
            )
          })()}
          {selectionStart && selectionEnd && (
            <rect
              x={Math.min(selectionStart.x, selectionEnd.x)}
              y={Math.min(selectionStart.y, selectionEnd.y)}
              width={Math.abs(selectionEnd.x - selectionStart.x)}
              height={Math.abs(selectionEnd.y - selectionStart.y)}
              fill={
                mode === ModeType.Create
                  ? 'rgba(37, 99, 235, 0.1)'
                  : 'rgba(37, 99, 235, 0.1)'
              }
              stroke={mode === ModeType.Create ? colors.blue[400] : colors.blue[500]}
              strokeWidth={1}
              strokeDasharray="4"
            />
          )}
          <defs>
            <pattern
              id="grid-pattern"
              width="50"
              height="50"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="2" cy="2" r="1.5" fill={colors.gray[500]} />
            </pattern>
          </defs>
        </g>
      </svg>
      {!hideZoomControls && (
        <ZoomControls
          placement="bottom-left"
          percent={displayZoom}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onSetPercent={handleSetZoom}
          onFit={handleFit}
          canZoomIn={displayZoom < ZOOM_MAX_PERCENT_VECTOR}
          canZoomOut={displayZoom > ZOOM_MIN_PERCENT}
          undoRedo={undoRedo}
        />
      )}
    </div>
  )
}

export default PnidRenderer
